import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import StatusBadge from './StatusBadge';
import ProgressBar from './ProgressBar';
import { LeadTimeDot, buildOrderDateTooltip } from './LeadTimeRisk';
// THE "is this order still racing a clock?" predicate — one definition in
// `constants/statuses.js`. It gates the urgency badge AND the lead-time dot.
import { showsTimeCriticalChrome, isPlacedStatus, isOrderComplete, RECEIVE_ENABLED_STATUSES } from '../constants/statuses';
import DeliveredOrderPill from './DeliveredOrderPill';
import { UserName } from './InactiveBadge';
import { getOrderRef, getOrderNumber } from '../utils/orderTitle';
import { formatDateCompact, formatDateDayMonth } from '../utils/dateFormat';
import { buildDeliveryProgressTooltip } from '../utils/deliveryTooltip';
import { lineProgressLabel } from './OrderItemReceivedControls';
import { useVatMode } from '../hooks/useVatMode';
// 🔀 The page-scroll table SHELL — the pinned header pane, the x-only rows, the
// sticky scrollbar, the scroll link, drag-to-pan, and the sortable + resizable
// header cells (`ui/SortableHeader`, `ui/ColumnResizeHandle`, the
// `constants/stickyTable` classes) — MOVED to `ui/PageScrollTable` (2026-09-27)
// so the hires Table view shares it. `OrderTableShell` below wraps it.
import PageScrollTable, { columnWidthOf, useEditingRowDim } from './ui/PageScrollTable';
import OrderAttachmentMarker from './OrderAttachmentMarker';
import { tightenPhoneColumns } from '../constants/phoneLayout';
import { OrderHeaderNoteContext } from './OrderSectionHeader';
import OrderDeliveryPaperworkMarker from './OrderDeliveryPaperworkMarker';
import { ExpandChevron, ExpandedPanelRow, EXPANDED_ROW_FRAME } from './TableRowExpansion';
import PoOrderTopLine from './PoOrderTopLine';
import PhonePoOrderCell from './PhonePoOrderCell';
import {
  expandColumn, poOrderColumn, requesterColumn, supplierColumn, statusColumn,
  PHONE_PO_ORDER_COLUMN,
} from './sharedTableColumns';
import {
  ROW_HOVER,
  chevronTitle,
  COMPLETED_ORDER_NUMBER_CLASS,
  RECEIVED_ITEM_NAME_CLASS,
} from '../constants/rowStyles';

/**
 * Spreadsheet-style table layout for the order list pages — the only order
 * list layout since the expandable card stack was retired (2026-10-18).
 *
 * ── Provenance, so nobody reverts this as a convention violation ──────
 * CLAUDE.md "UI Conventions → Order List Pages" says to use expandable cards
 * and NOT flat tables with column headers. **This table is an explicit
 * product-owner exception, made with that convention in front of him**
 * (2026-08-06), and the note recording it lives in that section. The column
 * names are lifted verbatim from the Google Sheets order log of a client
 * being migrated — Ordered By / Job Name / Supplier / Item / Quantity /
 * Unit Price / Total Price / Order Total / Status — so the list resembles the
 * sheet those users already live in. That familiarity is the point; do not
 * "improve" the labels. URL is deliberately absent (his call, for now).
 *
 * ⚠️ **PO is the ONE column that is NOT in that verbatim list (2026-08-08).**
 * Product owner: *"Looking at table view PO number should be its own column.
 * Make it the first column as this is important to the buyer."* So the
 * verbatim mapping now has exactly one deliberate addition, made by the owner
 * of the mapping. Recorded here and in CLAUDE.md's sanctioned-exception box so
 * that nobody "restores" the original nine as a tidy-up. Everything above
 * still governs the other nine labels.
 *
 * ── Pattern, not code ────────────────────────────────────────────────
 * Modelled on `components/gags/GagGroupTable.js`: a `<colgroup>`-sized table
 * in its own horizontal scrollport, a summary `<tr>` with a leading expand
 * chevron, and an expanded `<tr>` spanning every column. Shared pieces are
 * imported (`ROW_HOVER`, `StatusBadge`, `UserName`,
 * `getOrderRef`, `useVatMode`), not re-implemented. (It was `getOrderTitle`
 * until 2026-08-08 — see the `title` docblock in `OrderTableRow`.)
 *
 * ── The one design decision worth knowing ────────────────────────────
 * Their sheet is ONE ROW PER LINE ITEM with the order fields repeated. Here a
 * row is an ORDER, and the four item-level columns hold a fixed-height
 * scrollable box listing EVERY one of that order's items; expanding gives the
 * same items in the authoritative full-detail panel. That keeps the row count
 * equal to the order count (so filters, counts, pagination and `can_action`
 * all keep meaning) and needs no per-row request.
 *
 * ── Role-appropriate actions ─────────────────────────────────────────
 * There are none in this file, ON PURPOSE. The expanded panel is passed in
 * by the page as `expandedContent`. Every role/status gate therefore lives in
 * exactly one place per page. Never re-derive a permission here.
 */

/**
 * Columns, in order. `width` feeds a `<colgroup>` and the table is
 * `table-fixed`, so these are exact — which is what lets the four item
 * columns be spanned by ONE cell whose inner grid still lines up with their
 * headers (see ITEM_GRID below). Change a width here and the grid follows,
 * because both read this array.
 *
 * ── `sortField` — added 2026-08-08 with the clickable headers ──────────
 * Product owner: *"The headers in the table views should be clickable to order
 * by them. Usual ability to order by ascending and descending."*
 *
 * A column carrying `sortField` gets a clickable, keyboard-operable header; a
 * column without one renders as plain text with **no pointer cursor and no
 * click target** — nothing that looks clickable and does nothing.
 *
 * 🔴 **ONLY ORDER-LEVEL COLUMNS SORT. The four line-item columns deliberately
 * do NOT, and this is a settled product decision — do not re-open it.** A row
 * here is an ORDER that may hold six line items, so "sort by Quantity" has no
 * honest meaning. The owner was offered sort-by-first-item and
 * sort-by-aggregate and rejected both. Their headings stay visible so the
 * spreadsheet look survives.
 *
 * ⚠️ **`sortField` IS THE ROW KEY, WHICH IS NOT ALWAYS THE COLUMN KEY**, and
 * getting it wrong fails silently rather than loudly: the pages' sort reducers
 * end in `default: aVal = (a[sortField] || '')…`, so a name no row carries
 * compares `undefined` with `undefined`, sorts nothing, and leaves a header that
 * looks live. Two of the six differ from their column key and both were verified
 * against the payload, not guessed: `ordered_by` → **`requested_by`**, and
 * `job_name` → **`job_rig`** (`orders.job_rig` is the "Job / Rig / Gag" field).
 *
 * 💡 Why the mapping lives ON the column object rather than in a side map: the
 * nearest existing pattern, `components/gags/GagGroupTable.js`, keeps a
 * `HEADER_SORT` map because its `HEADERS` is a flat array of label STRINGS with
 * no per-column object to hang anything on — and keying by label makes a
 * user-visible rename a functional change. Here each column is already an
 * object, so one array stays the single source of truth for width, label,
 * alignment and sortability together. The header MARKUP below follows
 * `GagGroupTable` exactly, `aria-sort` included.
 */
export const ORDER_TABLE_COLUMNS = Object.freeze([
  // `fixed: true` (2026-09-28, `Fit columns to screen`): a pure icon/control
  // column the fit never resizes — mirrors the Breakdown's own `expand`
  // column (`constants/gagTableColumns.js`). See `utils/fitColumnWidths.js`
  // for what the flag does.
  //
  // 🔀 `fitFloor: true` (2026-09-28, owner ruling: *"none text, or known text
  // width columns first"*): PO, Date, Qty, Unit Price, Total Price, Order
  // Total and Status are all NON-TEXT or KNOWN-WIDTH (a badge, a number, a
  // money figure, a code) and pin at their `min` when fitting — the same
  // choice the Breakdown already made for Status/Shoot date/RA
  // (`constants/gagTableColumns.js`). Ordered By, Job Name, Supplier and Item
  // are free text and share whatever room `Fit columns to screen` leaves over.
  //
  // 🔀 2026-09-28: `expand`, PO, Ordered By, Supplier and Status are BUILT by
  // `components/sharedTableColumns.js` — the hires Table imports the same
  // builders, so the columns both tables show cannot drift. The docblocks
  // below stay here as the measurement record; the VALUES live there.
  expandColumn(),
  /**
   * PO — the FIRST DATA column (2026-08-08). Product owner: *"Looking at table
   * view PO number should be its own column. Make it the first column as this
   * is important to the buyer."*
   *
   * It sits after `expand` rather than at index 0 because `expand` is a
   * CONTROL, not data — a 40px chevron cell with an empty label. "First
   * column" means first thing the buyer reads across, which is this.
   *
   * ── Width: 60px, and it is 60 because of a MEASUREMENT ────────────────
   * ⚠️ **It was 120 for a few hours on 2026-08-08. The product owner saw it in
   * the browser and asked for it to be "as small as possible", so it is now the
   * smallest round number that a measured constraint allows.**
   *
   * 🔑 **First, the constraint he could not have known: `table-layout: fixed`
   * means a column CANNOT size to its content.** That is not a limitation to
   * work around — it is the property that makes the `<colgroup>` proportions,
   * and therefore the item-preview grid's alignment under these headers,
   * possible at all (see ITEM_GRID). `table-auto` would destroy both. So "auto
   * size" can only mean "pick a genuinely small fixed width and let longer
   * values truncate", which is the treatment Supplier (170) and Job Name (160)
   * already rely on: `truncate` + a `title` tooltip.
   *
   * **The binding constraint is the HEADER, not the data**, and it is bigger
   * than the two-character label suggests, because the header is now SORTABLE:
   * it carries the label plus `gap-1` (4px) plus the fixed-width sort caret
   * (`w-2.5`, 10px), inside `px-2` (16px total).
   *
   * Measured in Chromium against the COMPILED production stylesheet and the
   * production font (Inter 600 at `text-xs`, which `index.html` loads from
   * Google Fonts), by building this exact `<thead>` inside a `table-fixed` at
   * the colgroup sum — not estimated from character counts:
   *
   *   label "PO"            18.17px
   *   + gap-1                4.00px
   *   + caret (w-3)         12.00px   = 34.17px of content
   *   + px-2               16.00px    = **50.17px minimum cell width**
   *
   * ⚠️ **Re-measured 2026-08-08 when the caret box went `w-2.5` → `w-3`** (the
   * `▲` glyph measures 10.94px and had been overflowing a 10px box). The floor
   * rose 48.17 → 50.17 with it. The candidates, all measured:
   *   - 48 → content 32px, **overflows by 2.17px**
   *   - 50 → content 34px, **overflows by 0.17px** — below the floor
   *   - 60 → content 44px, **9.83px** of slack, and a multiple of 10 like every
   *          other width in this array
   *
   * **60 was already the right answer and is now the more clearly right one**:
   * before the caret grew, 50 fitted by 1.83px and was rejected for having under
   * a pixel per side of headroom against a webfont this repo does not host
   * (Google Fonts serves Inter as a variable font, metrics not frozen) and a
   * `text-xs` label a browser's *minimum font size* setting can enlarge. Two
   * pixels of caret later, 50 does not fit at all. The failure mode being bought
   * off is a clipped caret, and the caret is the only thing that says which way
   * the column is sorted.
   *
   * **Every realistic value still fits.** The dev database holds `001`, `002`,
   * `0012`, `147` — the widest of those is `0012` at 31.91px at `text-sm`,
   * inside a 44px content box. 118 of 120 orders have no PO at all.
   *
   * ⚠️ **The trade-off, stated because it is the cost of "as small as
   * possible":** the one 12-character outlier (`PO-BUYER-345`, 104.88px) is now
   * heavily ellipsed in the collapsed row — it needed ~121px, i.e. the width he
   * has just asked to halve. It is recoverable by hover (`title`) and in the
   * expanded panel. If he objects, THIS is the number to raise, and
   * `TABLE_MIN_WIDTH` plus `ITEM_GRID` both follow it automatically.
   *
   * 💡 A label rename would move the floor: "PO #" needs 60.11px and "PO No"
   * 71.33px of cell (measured the same way), so either would need this width
   * raised. Renaming a header is a product decision anyway — the labels are an
   * adoption lever (see the provenance note at the top of this file).
   */
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * 🔀 AMENDED AGAIN 2026-08-08 — IT IS A STACKED **TWO-LINE** CELL NOW, and
   * everything above this line describes the single-line 60px version it
   * replaced. The measurements above are kept because the METHOD is still how
   * to re-derive a width here; the number 60 is not current.
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Product owner: *"we need order ID's but I dont want another column thats
   * why showing both the PO and order number in the same column seemed like a
   * good idea"*, then *"Put PO on top"*.
   *
   * 🔑 **WHY ONE COLUMN RATHER THAN TWO, which is the part worth keeping.**
   * **1,728 of the 1,736 orders in the dev database have no PO number** (99.5%).
   * So a dedicated PO column is blank on almost every row while a dedicated
   * order-number column is full on all of them — two columns to say what one
   * cell can. Stacked, the cell always has content (the order number) and grows
   * a second line only where there is something to say. It also costs no row
   * height: Job Name already carried a second line, and this change MOVES that
   * line rather than adding one.
   *
   * ⚠️ **PO ON TOP IS THE OWNER'S EXPLICIT CALL, MADE AGAINST MY ADVICE — do
   * not "fix" it, and do not re-raise it without new information.** I argued for
   * the order number on top, on the 99.5% figure above: with PO leading, the top
   * line is empty on almost every row. His answer was *"Put PO on top"*, and it
   * is a legitimate reading — PO is what a buyer reaches for, and it should be
   * where they look. **The empty-top-line problem is solved in the CELL, not by
   * reordering:** an order with no PO renders the order number ALONE, vertically
   * centred, with no blank line above it. So the layout he asked for appears on
   * the 8 rows that have a PO, and the other 1,728 look deliberate rather than
   * broken. That conditional is the whole reason this works — see the cell.
   *
   * **Width 90, and the binding constraint changed with the content.** It is no
   * longer the header (label 18.17 + gap 4 + caret 10 = 32.17px of content, a
   * 48.17px floor) — it is the DATA, because both lines now carry a prefix.
   * `ORD-1736` is 8 characters at `text-xs`; `PO-0012` is 7 at `text-sm`. 90px
   * less `px-2` (16px) leaves a 74px content box, which fits both with room for
   * the four-digit order ids this database already has. Longer POs still ellipse
   * — `PO-BUYER-345` needs ~105px — and stay readable by hover and in the panel,
   * which is the same bargain Supplier (170) and Job Name (160) already make.
   *
   * 🔴 **The LABEL stays `PO`, not `PO / Order`.** A two-part label re-arms the
   * header-fits-its-column problem this file already records ("ORDER TOTAL"
   * spilling 11.22px into its neighbour), and it would buy nothing: the second
   * line carries its own `ORD-` prefix, so it says what it is. The label names
   * the top line, which is exactly what the owner put there.
   *
   * 🔀 **`sortField` is still `po_number`, and the ORDER NUMBER IS ITS
   * TIE-BREAK, not a second click.** The owner proposed a four-state header
   * (PO asc → PO desc → order asc → order desc) and asked for a view. It was
   * declined with reasons and he did not press it: a caret shows DIRECTION and
   * cannot show WHICH FIELD, so clicks 1 and 3 would render an identical header
   * sorting different things — the same objection this file already records
   * against a revert-to-default third click, but worse, because a hidden field
   * change surprises more than a hidden direction change. The real complaint
   * underneath it is that 1,728 blank POs left the list in arbitrary order; the
   * tie-break fixes that directly, with one caret and one meaning. See the
   * `po_number` arm of `compareOrderRows` in `utils/tableSort.js`.
   */
  /**
   * 🔀 **CYCLING HEADER (2026-08-08): `PO` → `Order` → wraps.** Product owner:
   * *"yes PO number and order ID is another great use case"*. The cell already
   * shows both values stacked, so the header names the LINE you are sorting by.
   * `id` sorts DESC — newest first, consistent with the default date sort.
   * ⚠️ The PO tie-break on order id (in both pages' reducers) is a DIFFERENT
   * mechanism and stays: sorting by PO still breaks ties on the order number.
   * The cycle adds a way to sort by the order number DIRECTLY; it does not
   * supersede the tie-break.
   */
  // `id` = the `Order` cycle state's row key on an ORDER row.
  // Its `min` 80 = the CYCLE label `Order` (75.81px of cell), not the resting
  // `PO` (50.17). Both measured in Chromium at `text-sm` in the production
  // font. Still correct after `ORD-<id>` became canonical (2026-08-12): the
  // column is bound by its HEADING, not its value — `ORD-2454` at `text-xs`
  // sits inside the 74px content box at 90 and inside 64px at this floor.
  poOrderColumn({ orderSortField: 'id' }),
  /**
   * Date — `orders.order_date`, rendered "25 Apr 26" (2026-08-08, the owner's own
   * format, so the two-digit year is the request and not a liberty).
   *
   * ── SECOND, immediately after PO. This is the APPROVED order ──────────
   * ⚠️ It was appended after Status for a few hours on 2026-08-08 and that was
   * **wrong**: I had no placement in the brief and inferred "append, so the nine
   * verbatim spreadsheet labels keep their order". The owner had in fact chosen
   * this layout from a preview he was shown, which reads
   *
   *     │ ›  │ PO ↕ │ Date ▼    │ Ordered By ↕ │
   *     │ ›  │ 147  │ 25 Apr 26 │ P Brittens   │
   *
   * so `expand · PO · Date · Ordered By · …` is what he picked, with that picture
   * in front of him. **Do not "restore" it to the end on the adjacency argument**
   * — that argument is real (the nine labels and their order are the adoption
   * lever) but it was outranked by an explicit choice, exactly as PO's own
   * position was by *"make it the first column"*. Two deliberate insertions at the
   * front; the other nine keep their relative order behind them.
   *
   * ── Width 90 — and the constraint here is the VALUE, not the heading ──
   * 🔑 **PO and Date are bound by OPPOSITE things, which is why they must never be
   * given a shared width "for consistency".** PO's two-character heading needs
   * more room than any PO value ever does (the sortable header's label + gap +
   * caret is 50.17px against ~32px of data), so its width is set by the HEADING.
   * Date is the mirror image: "DATE" is the shortest heading on the table
   * (65.47px of cell) while every row carries ~85px of date, so its width is set
   * by the DATA. A later reader will see 60 and 90 side by side and reach for one
   * number — the two columns are answering different questions.
   *
   * Measured in Chromium at `text-sm` in the production font:
   *   `25 Apr 26`  65.45px      ← the owner's example
   *   `30 Sep 26`  68.30px
   *   `08 Aug 26`  69.13px      ← widest realistic (two-digit day + wide month)
   * plus `px-2` (16px) = **85.13px of cell**:
   *   - 85 → content 69px vs 69.13px: **overflows by 0.13px**
   *   - 90 → content 74px, 4.87px of slack, and a multiple of 10 like the rest
   *
   * 🔴 **90, not the ~75 the brief estimated.** At 75 the content box is 59px and
   * every date with a wide month is ellipsed — and unlike a clipped PO (blank on
   * almost every row, recoverable on hover) a clipped date is on EVERY row and is
   * the entire point of the column. The 17.36px saved by the two-digit year is
   * what keeps even 90 possible; "25 Apr 2026" would have needed 110.
   *
   * ── No new sort plumbing ──────────────────────────────────────────────
   * Already on both list payloads via `SELECT o.*`, and `order_date` was already a
   * sort value with its own `case` in both pages' reducers
   * (including a `reference_number` tie-break); `handleSort` already seeds it
   * `desc`, so the header opens newest-first. It needed a `sortField` and nothing
   * else.
   *
   * `TABLE_MIN_WIDTH` is 1480 — unchanged by this move, since it is a `reduce`
   * over the array and does not care about order. Verified in Chromium against a
   * replica of `Layout.js`'s real chain that the PAGE does not scroll sideways at
   * 1024/1280/1440 with the sidebar both open and collapsed — see the note on
   * `OrderTableShell`.
   *
   * ── 🔀 90 → 100 ON 2026-08-08, when the cell became TWO LINES ──────────
   * The bottom line is `[8px dot][4px gap][required-by date at `text-xs`]`, and
   * the SECOND line is now the binding constraint, not the first:
   *
   *   top line    `08 Aug 26` at `text-sm`  69.13px          → 85.13px of cell
   *   bottom line 8 + 4 + (69.13 × 12/14)   71.25px          → 87.25px of cell
   *
   * (Glyph advance widths scale linearly with `font-size` for one font, so the
   * `text-xs` figure is the measured `text-sm` one × 12/14 — not a guess.)
   *
   * 🔴 **90 would have FITTED, by 2.75px, and was rejected anyway — for the same
   * reason 50 was rejected for PO.** That column's docblock records 1.83px of
   * slack as "under a pixel per side against a webfont this repo does not host",
   * and this is the same order of margin on a line that appears on nearly every
   * row. Worse here: 8px of that line is a DOT, a fixed reservation that cannot
   * ellipse — so any shortfall clips the DATE rather than degrading gracefully.
   * 100 gives a 84px content box and 12.75px of slack.
   *
   * 🪞 **A FIGURE HERE WAS WRONG AND IS CORRECTED — 2026-08-08.** This said the
   * cycling-sort header would need *"~94px of cell, which 90 does not give and
   * 100 does"*, and used that as supporting evidence. **The 94 was arrived at by
   * scaling 49.47 — which is "DATE"'s label PLUS `gap-1` PLUS the caret — and
   * then adding the gap and caret again.** Double-counted.
   * The real figure: the LABEL "DATE" is **33.47px**, so a five-character
   * uppercase label is ≈41.8px, and a sortable header needs
   * `label + 4 (gap-1) + 12 (w-3 caret) + 16 (px-2)`, i.e. **≈74px of cell** —
   * which **90 already gave**. So a longer header label was never a reason to
   * widen this column.
   * ✅ **The conclusion is unchanged and was never resting on it**: the width
   * moved for the SECOND LINE (71.25px of content against 90's 74px box, a
   * 2.75px margin this file rejects elsewhere). That argument stands alone,
   * which is exactly why the header point was recorded as "a consequence, not
   * the justification". Keeping the correction visible because a wrong number
   * quoted as support is how a right decision acquires a false reason.
   */
  /**
   * 🔀 **CYCLING HEADER (2026-08-08): `Date` → `Due` → `Slack` → wraps.**
   * Product owner: *"clikcing on date sort in the orders list could cycle
   * orderd date, due date and slack… if we do this the title should change
   * from 'date' when we cycle to 'Due' and 'Slack'"*. The cell shows both
   * dates stacked with the slack dot beside the deadline, so **every one of
   * the three states names something physically in this cell** — which is what
   * makes a changing label honest rather than a hidden mode.
   *
   * ✅ **THE THIRD STATE LANDED 2026-08-08 AND IT WAS DATA, NOT CODE — ONE
   * ENTRY IN THIS ARRAY AND NOTHING ELSE.** It had been deferred because
   * `ui/SortableHeader`'s cycling mechanism and `min_slack_days` as a sort
   * field were built on two branches that could not see each other; both are
   * merged, so the note that used to stand here (*"deliberately absent…
   * adding it here as well would collide"*) is spent. Kept as a record of the
   * outcome: the component was built to take N fields precisely so that the
   * cost of the third state would be this line. **No new reducer `case`, no
   * new sort-option entry, no new term in either page's
   * `handleSort` — all three already existed.**
   *
   * 📐 **`asc` IS MOST-AT-RISK-FIRST, and it is not a stylistic default.**
   * `min_slack_days` is `gap - lead`, the days of headroom before this order
   * has to be PLACED, so **smaller is more urgent and it goes NEGATIVE** for
   * one already too late. Ascending puts the orders you can still do something
   * about at the top. 🔑 The direction here is also what makes this state
   * immune to the pages' `handleSort` seeding: a cycling column passes an
   * EXPLICIT direction, which returns before the `order_date`/`order_total`
   * `desc` seed is ever consulted.
   *
   * 🪤 **`min_slack_days` MUST NOT JOIN THE BLANKS-LAST `case` GROUP in either
   * page's reducer, and it does not.** That group tests emptiness with
   * `!value`, which is right for its four TEXT fields and catastrophic here:
   * **`0` is a REAL slack value** — "no slack, order today", the RED band in
   * `components/LeadTimeRisk.js` — and `!0` is `true`, so a falsy test would
   * grade the most urgent not-yet-overdue order on the page as blank and sink
   * it to the bottom of the very sort that exists to surface it. Both pages
   * use `== null`, as the urgency tie-break's `?? 999` already did.
   */
  {
    key: 'order_date',
    // 🔀 "Raised", not "Date" (owner 2026-11-02): all three states are dates.
    label: 'Raised',
    width: 100,
    /**
     * 🚨 **90, NOT the 80 its heading alone would give — and this is the HEADER
     * RULE APPLIED, not an exception to it.**
     *
     * Its widest heading is the CYCLE label `Slack` (75.14px of cell → 80). But
     * the cell's SECOND line is `8px dot + 4px gap + the date at text-xs` =
     * 71.25px, needing an 87.25px cell. **The dot is a fixed-size non-text
     * element that cannot ellipse** — structurally the same fact as the sort
     * caret's `shrink-0`, which is what the header floor exists to protect. At
     * an 80px floor the 12px of dot-plus-gap is spent first and the DATE is
     * clipped to ~52px on every row.
     *
     * This is the one column on this table whose cell contains a non-text fixed
     * element, so it is the one place the rule bites on the data side. Do not
     * "correct" it down to 80 for consistency with the other eleven.
     */
    min: 90,
    align: 'left',
    fitFloor: true,
    sortField: 'order_date',
    fields: [
      {
        sortField: 'order_date',
        label: 'Raised',
        // `orders.order_date`: stamped server-side (UTC day) by `POST /api/orders`
        // and `/:id/duplicate` and never edited after — there is no form field and
        // PUT does not touch it (corrected 2026-11-02; this said "settable on the
        // form"). It differs from `created_at` only on imported rows (department
        // restore, the Laird migration, demo seeds), which keep their own dates.
        description: 'Date the order was submitted.',
        direction: 'desc',
      },
      {
        sortField: 'required_by_date',
        label: 'Due',
        // 🚨 **NOT "the earliest date any item is needed by" — that reading is
        // WRONG and is explicitly refuted at the cell and in
        // `client/src/pages/CLAUDE.md`.** `required_by_date` is
        // `computeSlackDetail`'s `neededByDate`, captured in the SAME branch
        // that lowers the minimum slack (`server/helpers/leadTimeHelpers.js`),
        // so it is the deadline of the item that WINS the minimum — which is a
        // different item from the earliest-dated one whenever lead times
        // differ (needed 30 Sep with a 60-day lead beats needed 10 Sep with
        // none). It is by construction the date the slack dot beside it is
        // about, which is the only thing that makes the two-line cell honest.
        description: 'The needed-by date of the item with the least time to spare.',
        direction: 'asc',
      },
      // Worst first — see the `asc` note above. The label is the short word
      // deliberately: it is also the `Sort ▾` trigger's text, on a bar the
      // owner has twice complained wraps.
      {
        sortField: 'min_slack_days',
        label: 'Slack',
        // `slack = daysUntilNeeded - leadDays` (`computeSlackDetail`), i.e. how
        // long you may still wait before PLACING the order and have it arrive
        // by the Due date above. 🪤 **`0` is a real, reachable value and the
        // sentence must say what it means** — it is the same `0`-is-not-absent
        // trap as the reducer's `== null`; a reader who assumes 0 means "no
        // data" reads the most urgent row on the page as blank.
        // 🔻 Deliberately says nothing about the DOT. The dot is hidden on a
        // delivered/cancelled order (`showsTimeCriticalChrome`) while the
        // column and this sort are not, so a clause about it would describe a
        // different thing from the one the header sorts by.
        description: 'Days left to place this order to hit its due date. 0 means order today, below zero is late.',
        direction: 'asc',
      },
    ],
  },
  // Column key `ordered_by`, ROW key `requested_by` — see the docblock.
  requesterColumn({ key: 'ordered_by', label: 'Ordered By', width: 150, min: 120 }),
  // Column key `job_name`, ROW key `job_rig` — see the docblock.
  { key: 'job_name', label: 'Job Name', width: 160, min: 110, align: 'left', sortField: 'job_rig' },
  // 🔀 170 → 140 (2026-09-29, owner: ~20% narrower; min stays 100).
  supplierColumn({ width: 140 }),
  // ── The four LINE-ITEM columns. No `sortField`, by product decision, because
  // a row is an order that may hold six items. Do not add one.
  { key: 'item', label: 'Item', width: 260, min: 50, align: 'left' },
  /**
   * 🔻 THE SEPARATE DECISION THE PREVIOUS NOTE DEFERRED IS NOW MADE — 80 → 60.
   *
   * That note read *"`min`/`width` stay 80 regardless — whether to lower them
   * is a SEPARATE decision, not made here."* Product owner, 2026-09-13:
   * *"Column QTY should be narrow by default to save width."*
   *
   * ── Where 60/50 come from, MEASURED (see `OrderTableView.columnMinimums.test.js`) ──
   *   HEADER floor  "QTY" 25.78 + `px-2` 16          = **41.78** → min **50**
   *   CONTENT floor `9999` at `text-sm`/400 + `px-2` = **50.73** → width **60**
   *
   * 🚨 **THE CONTENT FLOOR IS THE BINDING ONE HERE, AND IT IS THE ONLY COLUMN
   * ON THIS TABLE WHERE THAT IS TRUE.** Everywhere else this file's floor rule
   * ("the floor is the COLUMN'S OWN HEADING, never its data") holds because the
   * data `truncate`s. **The preview's quantity span does NOT** — `ItemPreview`
   * gives `truncate` to the description span alone, and the three numeric spans
   * sit in definite percentage tracks, so a value too wide for its track spills
   * LEFT (they are `text-right`) over the neighbouring column instead of
   * ellipsing. That is why `width` is set from the data here and `min` is not:
   * `min` still guards the heading, which is the thing that can never shrink.
   *
   * 🪤 `item.quantity` is rendered RAW (`{item.partial ? '—' : item.quantity}`),
   * with no `toLocaleString`, so there are no thousands separators to pay for
   * and a five-digit value is `99999`, not `99,999`. Re-measure if that changes.
   */
  { key: 'quantity', label: 'Qty', width: 60, min: 50, align: 'right', fitFloor: true },
  /**
   * 🟢 UNCHANGED at 100/90, and that is a MEASURED result rather than an
   * omission from the 2026-09-13 narrowing. CONTENT floor `£99,999.99` =
   * **92.50px** of cell (`text-sm`/400 + `px-2`); HEADER floor "UNIT PRICE" =
   * **89.22** → min 90. `max(89.22, 92.50)` rounded up to the next ten is 100,
   * which is what it already declared. It is the one column of the five the
   * owner named that was already at its minimum.
   */
  { key: 'unit_price', label: 'Unit Price', width: 100, min: 90, align: 'right', fitFloor: true },
  /**
   * 110 → 100 (2026-09-13). CONTENT floor `£99,999.99` = 93.92px of cell at
   * `text-sm`/400 inside the preview's `px-2`; HEADER floor "TOTAL PRICE" =
   * 99.41px. 100 is `max(99.41, 93.92)` rounded up to the next ten — this
   * column is HEADER-bound, so `min === width` now and it can only widen.
   */
  { key: 'total_price', label: 'Total Price', width: 100, min: 100, align: 'right', fitFloor: true },
  /**
   * 🚨 **130, not 110 — a DEFECT FIX, and it was introduced by the sortable
   * headers themselves a commit earlier on 2026-08-08.**
   *
   * "ORDER TOTAL" is the longest label on the table: 89.22px at `text-xs`
   * uppercase with `tracking-wider`, plus `gap-1` (4px) and the caret (10px) =
   * **103.22px of content**, which needs 119.22px of cell. At 110 the content
   * box is 92px, so the header spilled **11.22px** out of its own cell and
   * overlapped the "TOTAL PRICE" heading beside it at every table width at or
   * near the minimum.
   *
   * 🪤 **`scrollWidth` REPORTED A CLEAN FIT, and that is why this shipped.**
   * The right-aligned headers are `justify-end`, so the overflow goes to the
   * inline-START side — and `scrollWidth` does not report overflow in that
   * direction. The measurement said `scrollWidth === clientWidth` for a header
   * that was visibly overlapping its neighbour. It was only caught by comparing
   * the CHILD EDGES to the button's content box. **Any future check of "does
   * this header fit" must do the same** — see §6 of
   * `OrderTableView.sortableHeaders.test.js`, which pins the measured need of
   * every sortable label against its declared width.
   *
   * 130 gives 8.78px of slack and is a width this table already uses (Status).
   */
  // `min === width`: header-bound already (121.22 → 130), so it widens and
  // never narrows. `aria-valuemin` on its handle says so.
  //
  // 🟢 UNCHANGED at 130/130 by the 2026-09-13 narrowing, MEASURED not skipped:
  // CONTENT floor `£999,999.99` at `text-base`/600 + `px-2` = **116.92px**,
  // which is BELOW the 121.22 its own heading needs. The heading is still the
  // binding constraint, so 130 already IS "the minimum possible" for this
  // column and there is nothing here to give back. (A seven-figure total,
  // `£9,999,999.99`, measures 131.64 and would overflow — no order on this
  // system is near that, and the day one is, this column widens, not the rule.)
  { key: 'order_total', label: 'Order Total', width: 130, min: 130, align: 'right', sortField: 'order_total', fitFloor: true },
  /**
   * 🚨 **130 → 120, AND THE WIDTH WAS BOUGHT BY REWORDING THE LABEL, NOT BY
   * WIDENING THE COLUMN. The owner chose that, and it is the more interesting
   * half of this entry.**
   *
   * Owner, 2026-09-13: *"Defualt unit price and total price and order total and
   * status to the minimum possible"*. "Minimum possible" is
   * `max(header floor, content floor)` — the narrowest width at which the
   * column still renders what it holds. Measuring that surfaced a defect rather
   * than a saving: the widest `StatusBadge` label, `Partially Delivered`,
   * needed **137.30px** of cell against a declared 130, so Status would have
   * had to GROW to 140. Shown that, he ruled: *"maybe change the wording to
   * something shorter like 'Partial delivery'"*.
   *
   *   HEADER floor  "STATUS" 50.44 + gap 4 + caret 12 + `px-2` 16 = **82.44** → min **90**
   *   CONTENT floor the widest `StatusBadge` — now `Partial delivery`, badge
   *                 element 102.73px (its own `px-2` included) + the cell's
   *                 `px-2` 16 = **118.73** → width **120**
   *                 (was 137.30 → 140 while the label read `Partially Delivered`)
   *
   * 🪤 **THE OLD LABEL DID NOT CLIP AT 130 — IT WRAPPED, WHICH IS WHY NOTHING
   * CAUGHT IT FOR FIVE WEEKS.** Measured in headless Chromium against the
   * compiled stylesheet at a real `table-fixed` colgroup: at 130 the old badge
   * rendered on **two lines** (box 114 × 36px) and the whole ROW grew 36px →
   * 52px, on every `partially_delivered` order, on both table pages. There is
   * no overflow for `scrollWidth` — or for a header-fit ratchet — to report,
   * because the `flex flex-wrap` wrapper absorbs it. **Any future check of
   * "does this badge fit" must measure the badge BOX, not look for overflow.**
   *
   * 🪤 **120 IS MEASURED AND IS NOT THE OBVIOUS NUMBER.** `Partial delivery`
   * still wraps to two lines at 100 AND at 110 (badge boxes 84 × 36 and
   * 94 × 36) — a shorter label is not automatically a short one. The next
   * widest labels are `Submitted` 91.45 and `Cancelled` 90.03, so this column's
   * own longest label is still the binding value; that was re-checked, not
   * assumed.
   *
   * `min` stays **90** (the header floor): a user who wants Status narrow may
   * still drag it there and accept the wrap. 120 is the DEFAULT, not a bound.
   *
   * 🔻 Net effect of the whole 2026-09-13 retune: −40px of table
   * (qty −20, total price −10, status −10). `ORDER_TABLE_MIN_WIDTH` 1520 → 1480.
   */
  statusColumn({ sortField: 'status', width: 120, min: 90 }),
]);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 WHERE EVERY `min` ABOVE COMES FROM — MEASURED, NOT CHOSEN
 * ═══════════════════════════════════════════════════════════════════════════
 * **The floor is the COLUMN'S OWN HEADING, never its data**, for the reason
 * `order_total` taught this table the expensive way: data truncates (Job Name,
 * Supplier and the item description all carry `truncate` + a `title`), but a
 * HEADING cannot — `TH_BASE` is `whitespace-nowrap` and the caret is
 * `shrink-0` — so a column dragged under its own heading spills out of its
 * cell. A column a user can drag is a column a user can drag under its heading,
 * so the floor has to be ENFORCED rather than merely chosen well.
 *
 * Measured 2026-08-10 in headless Chromium (Playwright) against the COMPILED
 * `client/build/static/css/main.*.css` and the production webfont (Inter 600 at
 * `text-xs`, uppercase, `tracking-wider`), by building BOTH of
 * `ui/SortableHeader`'s branches inside a `table-fixed` at the colgroup sum.
 * 🪤 **`scrollWidth` REPORTS A FALSE FIT ON A RIGHT-ALIGNED HEADER** — those are
 * `justify-end`, so overflow goes to the inline-START side, which `scrollWidth`
 * does not account for. The harness compares CHILD EDGES (the label rect ∪ the
 * caret rect) against the button's computed content box.
 *
 * Each `min` is the widest label's requirement **rounded UP to the next ten**:
 *
 *   ── sortable (label + `gap-1` 4px + the `w-3` caret 12px, inside `px-2` 16px)
 *   PO       "Order" 75.81→80  ·  Date "Slack" 75.14→80 (**raised to 90**, see
 *                                  the dot note at the column)
 *   Ordered By      114.25→120 ·  Job Name    100.09→110
 *   Supplier         94.13→100 ·  Order Total 121.22→130 · Status 82.44→90
 *
 *   ── NON-sortable (plain `<th px-2 py-2>`: no gap, no caret)
 *   Item  47.98→50 · Qty 41.78→**50** (🔻 was 80 until 2026-09-13; the owner
 *                                  ruling that deferral pointed at has landed) ·
 *   Unit Price 89.22→90 · Total Price 99.41→100
 *
 * 🔻 **AND THE OPENING CLAIM ABOVE IS TRUE OF EVERY `min` BUT NOT OF EVERY
 * `width` ANY MORE (2026-09-13).** `min` is still the heading everywhere — that
 * is the correctness bound and it is what this block enumerates. `width` is now
 * `max(heading, widest realistic VALUE)`, because the owner asked the five
 * money/quantity/status columns to default to "the minimum possible" and a
 * default that clips its own data is not a minimum, it is a defect. On four of
 * the five the heading still wins; on `quantity` the data does. The per-column
 * derivations, the measurement date and the harness are in
 * `OrderTableView.columnMinimums.test.js`, which pins all five as literals.
 *
 * 🚨 **PO AND DATE ARE CYCLING COLUMNS AND THEIR BINDING LABEL IS NOT THE
 * RESTING ONE.** PO renders `PO` (50.17) or **`Order` (75.81)**; Date renders
 * `Date` (65.47), `Due` (58.56) or **`Slack` (75.14)**. A floor derived from
 * `c.label` alone is 30px too low on PO and 10px too low on Date, and the
 * column would only spill in a sort state nobody screenshots. §6 of
 * `OrderTableView.sortableHeaders.test.js` pins the RESTING label only; the
 * all-labels ratchet is `OrderTableView.headerFit.test.js`.
 *
 * 🚨 **`expand` IS THE ONE COLUMN WITH NO MEASURED FLOOR, AND THAT IS STATED
 * RATHER THAN PAPERED OVER.** It has no heading text (`label: ''`), so the
 * harness has nothing to measure. Its `min` is **its own shipped width, 40** —
 * a number already known to hold its chevron because it has been rendering it.
 * It can be widened, never narrowed. Guessing something smaller for a column
 * whose content is an icon is exactly the invented number this file's other
 * measurement notes warn against.
 *
 * ⚠️ **A `min` EXPIRES WITH ITS LABEL.** Rename a heading, add a cycle state,
 * change the caret box (`w-3`), `TH_BASE`'s size/weight, or the `px-2`, and
 * every figure above moves together. Re-measure; do not adjust one by eye and
 * do not reason a new value from an old one.
 */

/**
 * The widest a user may drag ANY order column. One ceiling, not twelve.
 *
 * 🔑 **Why one number.** A minimum is a CORRECTNESS bound — the width below
 * which a specific heading overflows — so it is necessarily per column. A
 * maximum is not: no width is *wrong*, only unreasonable, and "unreasonable"
 * does not vary by column. Twelve hand-picked ceilings would be twelve numbers
 * with no measurement behind them.
 *
 * **Why 600.** The widest default here is Item at 260, so 600 is well over
 * twice the widest column anyone has argued for, and it caps the whole table at
 * 7,200px rather than the 60,000 a hand-edited `localStorage` value would give
 * inside a horizontal scroll region.
 *
 * ⚠️ **Deliberately NOT imported from `constants/gagTableColumns.js`.** That
 * file's `MAX_GAG_COLUMN_WIDTH` happens to be 600 too, and the two are a
 * COINCIDENCE rather than a shared rule — they bound different tables with
 * different widest columns. Importing it would couple two numbers that must be
 * free to move apart, which is the trap this file already records for PO and
 * Date both landing on 90.
 */
export const MAX_ORDER_COLUMN_WIDTH = 600;

/**
 * `localStorage` key for the user's order-table column widths.
 *
 * 🔑 **GLOBAL, not per page, and that follows from the surface rather than from
 * a preference.** Department Orders and To Approve render the SAME twelve
 * columns from THIS ONE component, so a per-page key would let one component's
 * two callers disagree about one table. It is a display preference of the
 * same kind as `sfx_dark_mode` and `sfx_page_size`.
 *
 * ⚠️ **Separate from `sfx_breakdown_col_widths` and it must stay separate** —
 * different columns entirely, and the map is keyed by column key, so sharing a
 * key would give each surface a map full of entries the other's `sanitise`
 * discards. The two surfaces' widths have nothing to say to each other.
 *
 * 🚨 **The VALUE IS A MAP KEYED BY COLUMN KEY, NEVER AN ARRAY BY INDEX.** An
 * index array mis-assigns every width after any column is added, removed or
 * reordered — and this table has already had TWO columns inserted at the front
 * (PO and Date, both on 2026-08-08). Unknown keys are dropped and missing ones
 * fall back to the measured default, which is what makes the format
 * self-healing and is why it carries no version field.
 *
 * 🪤 **It is passed as a CONSTANT, so `npm run check-bloat` cannot see it in
 * EITHER direction** — its orphan-key scan is a string-LITERAL regex
 * (`scripts/check-bloat.js:230`). **Grep `sfx_order_table_col_widths` by hand**
 * before concluding it is unused. 41 `localStorage.setItem(CONST, …)` sites
 * under `client/src` already share that blind spot, and `sfx_breakdown_view`
 * was deleted in 2026-08-07 precisely because a constant-keyed key went
 * invisible to the scan.
 */
export const ORDER_COL_WIDTHS_KEY = 'sfx_order_table_col_widths';

/**
 * MY ORDERS' OWN WIDTH KEY (2026-08-12).
 *
 * 🚨 **A SEPARATE KEY IS REQUIRED BY THE MECHANISM, NOT CHOSEN AS A
 * PREFERENCE — SHARING IT WOULD SILENTLY CORRUPT THE OTHER TWO PAGES.**
 * `useColumnWidths`'s `sanitise` builds a FRESH map from the CALLER's own
 * `columns` and copies nothing else, and the hook writes the whole map back on
 * every change. So an eleven-column page sharing `sfx_order_table_col_widths`
 * would, **on the user's first drag anywhere on My Orders**, write back a map
 * with `ordered_by` absent — and Department Orders and To Approve would find
 * that column reset to its default. Silently, for every user, with nothing red
 * anywhere.
 *
 * 🕶️ **THE REASONING THAT JUSTIFIED SHARING IS NOT WRONG, IT EXPIRED.** Until
 * 2026-08-12 the root `CLAUDE.md` justified one key with *"all three render the
 * SAME twelve columns from the SAME component"* — and that was the correct
 * argument for exactly as long as the premise held. **The premise is the column
 * SET, so the moment two surfaces diverge the key must diverge with them.** Same
 * shape as `sfx_breakdown_col_widths`, which is separate from the order key for
 * this identical reason, written up there as *"different columns entirely, so
 * one shared key would give each surface a map full of entries the other's
 * `sanitise` discards"*.
 *
 * 🪤 **Passed as a CONSTANT, so `check-bloat` sees it in NEITHER direction** —
 * grep `sfx_my_orders_col_widths` by hand before concluding it is unused.
 *
 * 🔴 **The cost, accepted and stated: a width dragged on Department Orders no
 * longer follows the user to My Orders.** That is the unavoidable price of the
 * two surfaces genuinely differing; the alternative is the corruption above.
 */
export const MY_ORDERS_COL_WIDTHS_KEY = 'sfx_my_orders_col_widths';

/**
 * The defaults, in the same `{ [columnKey]: px }` shape the hook and
 * `localStorage` use. A map, never an array — see the key's docblock.
 */
export const DEFAULT_ORDER_COLUMN_WIDTHS = Object.fromEntries(
  ORDER_TABLE_COLUMNS.map((c) => [c.key, c.width])
);

/**
 * The four columns the item preview spans, as one contiguous block.
 *
 * 🔑 **This is a KEY FILTER, not a slice or a count, and that is what makes
 * adding a non-item column safe.** `ITEM_COLUMNS` is whatever
 * `ORDER_TABLE_COLUMNS` holds under these four keys — so the PO column added
 * on 2026-08-08 changed `ORDER_TABLE_COLUMNS.length` (10 → 11) and left this
 * array, the item block (550 at the time, **520 since the 2026-09-13 width
 * retune** — `orderItemBlockWidth()` computes it; never quote it) and every
 * `ITEM_GRID` track numerically
 * untouched. Never re-express it as an index range or a hard-coded 4: the
 * whole point is that a column inserted anywhere outside the block is invisible
 * to the preview's geometry.
 */
// 🔀 `NARROW_COLUMN_RESIZE_QUERY` (the breakpoint below which no resize handle
// is offered) MOVED to `components/ui/PageScrollTable.js` with the shell body
// (2026-09-27), still byte-identical to `GagGroupTable`'s `NARROW_QUERY`.

const ITEM_COLUMN_KEYS = ['item', 'quantity', 'unit_price', 'total_price'];
const ITEM_COLUMNS = ORDER_TABLE_COLUMNS.filter((c) => ITEM_COLUMN_KEYS.includes(c.key));

/**
 * One column's LIVE width: the user's stored value when there is a usable one,
 * otherwise the measured default.
 *
 * 🪤 **`Number.isFinite`, never a truthiness or a `>=` test.** `localStorage` is
 * user-writable and `JSON.parse` hands back anything: `'120' >= 70` is **true**,
 * so a comparison-only guard accepts the STRING, `width: "120"` renders by
 * accident, and the first `reduce` over the map concatenates instead of adding.
 * Same rule `useColumnWidths.sanitise` applies, restated here because this
 * function is also reached with a `null` map (no hook, first paint, or a row
 * rendered outside `OrderTableShell`).
 */
// 🔀 One definition since 2026-09-27: `columnWidthOf` in `ui/PageScrollTable`,
// which the shell's `<colgroup>` reads too — two copies of "the live width"
// could disagree about one column.
const orderColumnWidth = columnWidthOf;

// The expand column has no heading text: the header announces "Expand" and its
// resize handle borrows that name — `ui/PageScrollTable`'s DEFAULTS since
// 2026-09-28 (`defaultHeaderAriaLabel` / `defaultResizeLabel`); this file's
// own copies are deleted.

/** The item block's total, i.e. what the four spanned columns are worth together. */
export const orderItemBlockWidth = (widths = null) =>
  ITEM_COLUMNS.reduce((sum, c) => sum + orderColumnWidth(widths, c), 0);

/**
 * The inner grid's track sizes, derived from the SAME widths the `<colgroup>`
 * uses — but as PROPORTIONS of the spanning cell, not pixels, so the preview
 * rows sit exactly under Item / Quantity / Unit Price / Total Price at ANY
 * table width. The spanning cell carries `p-0` and each grid cell its own
 * `px-2`, matching the `<th>` padding — without that the grid would be inset
 * by the cell padding and every column would be 8px out.
 *
 * ── ⚠️ REWRITTEN 2026-08-08, BECAUSE THE TABLE BECAME FLEXIBLE ────────
 * These tracks used to be the literal pixel widths (`260px 80px 100px
 * minmax(0, 110px)`, summing to exactly 550px) on the reasoning that
 * `table-fixed` + a `<colgroup>` made the columns exact. The table now also
 * carries `w-full`, so on any screen wider than TABLE_MIN_WIDTH the browser
 * distributes the spare width across the columns **in proportion to their
 * specified widths** (fixed table layout, the classic stretch-to-fit) — every
 * column becomes `width × tableWidth / TABLE_MIN_WIDTH`. A hard-coded 550px
 * grid under stretched headers is silently misaligned on every wide screen,
 * and jsdom cannot see it.
 *
 * The proportions survive the stretch exactly, because the same scale factor
 * applies to every column: track `i` is `c.width / ITEM_BLOCK_WIDTH` of the
 * block before the stretch and after it. So percentages of the spanning cell
 * are correct at every width, and they stay DERIVED from `ORDER_TABLE_COLUMNS`
 * — change a width up there and this follows, which is the whole design and
 * the reason a second copy of these figures must never be written here.
 *
 * ── ⚠️ THE LAST TRACK IS `minmax(0, …)` AND THAT IS THE WHOLE FIX FOR THE
 * HORIZONTAL SCROLLBAR (2026-08-07). Do not "tidy" it to a bare `1fr`. ──
 *
 * The original bite, kept because the cause has not gone away: the preview box
 * is `overflow-y-auto`, and two CSS facts turned that into a horizontal
 * scrollbar on every multi-item row:
 *
 *   1. Setting overflow on ONE axis forces the other out of `visible` — a box
 *      with only `overflow-y` set computes `overflow-x: auto`. So the preview
 *      has always been horizontally scrollable, not just vertically.
 *   2. The vertical scrollbar consumes 15px of CLIENT width (classic
 *      scrollbars — Windows/Linux, and macOS with "always show" set). The
 *      grid still demanded 550px inside a 535px client box.
 *
 * Measured in Chromium against a replica of that exact node tree, NOT reasoned
 * about: clientWidth 535 vs scrollWidth 550, i.e. 15px of overflow, on a row
 * whose descriptions were all short enough that NOTHING was truncating. The
 * symptom was independent of text length — a track-sizing bug, not a
 * truncation one.
 *
 * 🔑 It also stole 15px of the box's HEIGHT: a 52px (3.25rem) box measured
 * clientHeight 37px, which is less than the TWO preview lines plus a sliver
 * that CLAUDE.md records as a product decision. Removing the horizontal
 * scrollbar restored the full 52px. The height, the scroll condition and the
 * drag guard were untouched by that fix and are untouched by this one.
 *
 * 💡 Percentages make that overflow STRUCTURALLY impossible rather than merely
 * absent, which is a real improvement over the pixel version: the four tracks
 * resolve against the box's CONTENT width, which already excludes the vertical
 * scrollbar, so they cannot out-demand the space they are given. The
 * `minmax(0, …)` on the last track is still not optional — it is what absorbs
 * the sub-pixel rounding of four percentages, and it is what keeps the
 * automatic-minimum-size rule disarmed. A track whose MIN sizing function is
 * `auto` (which is what a bare `1fr` means) re-arms that rule for the three
 * numeric cells, letting a long money value widen the track and put the
 * scrollbar straight back. A definite 0 minimum cannot. **`minmax(0, X)` is
 * fine; `1fr` and `auto` are not.**
 *
 * Why the LAST track carries the flexibility and not the first: Item /
 * Quantity / Unit Price then stay aligned with their `<th>`s and only Total
 * Price's right edge can pull in. Flexing the first track instead would shift
 * all three of the others out of line with their headers.
 */
/**
 * ── 🚨 REWRITTEN AGAIN 2026-08-10, BECAUSE THE WIDTHS BECAME USER-ADJUSTABLE ──
 *
 * These were module-level constants computed ONCE from the DECLARED widths.
 * That was correct for as long as the declared widths were the only widths.
 * **The moment a USER can drag a column, a static grid stops lining up with its
 * own headers — on every row, at every table width, and jsdom can see none of
 * it.** Drag Quantity 60 → 160 and the real block is 620 with Quantity at
 * 25.81%, while a frozen `ITEM_GRID` still says 60/520 = 11.54%.
 * (🔻 The worked example was written as 80 → 160 / 630 / 25.40% / 80/550 =
 * 14.55% against the pre-2026-09-13 defaults. Re-derived, not adjusted by eye.)
 *
 * 🟢 **The proportions still survive the stretch exactly, and that property is
 * what makes a live derivation sufficient rather than merely better.** Under
 * `table-fixed` every column is scaled by ONE common factor
 * (`tableWidth / declaredTotal`), so `track_i / blockRendered` equals
 * `declared_i / declaredBlock` before and after the stretch, in both regimes
 * (table at its floor, and table stretched to fill a wider scrollport). Only
 * the SOURCE of the declared numbers changes here.
 *
 * 🔑 **And the old invariant is intact: a change to any NON-item width still
 * cannot move these tracks** — a non-item drag changes the common factor, which
 * cancels. Only an item column's own width can, which is exactly what the
 * `ITEM_COLUMN_KEYS` filter above is for.
 */
const ITEM_TRACK_PCT = (widths, c, block) =>
  `${((orderColumnWidth(widths, c) / block) * 100).toFixed(4)}%`;

/**
 * The item preview's four grid tracks, as percentages of the spanning cell.
 *
 * Pure and exported so jsdom can pin the ARITHMETIC even though it can never
 * see the ALIGNMENT — that stays a browser check and the tests say so.
 *
 * ⚠️ **THE LAST TRACK KEEPS `minmax(0, …)`. Do not "tidy" it to a bare `1fr`.**
 * A bare `1fr` means `minmax(auto, 1fr)`, and an `auto` minimum re-arms the
 * automatic-minimum-size rule on the three numeric cells, which is what put a
 * measured 15px horizontal scrollbar in this box in 2026-08-07 — and stole 15px
 * of its HEIGHT with it. A definite 0 minimum cannot. `minmax(0, X)` is fine;
 * `1fr` and `auto` are not. Why the LAST track and not the first: Item /
 * Quantity / Unit Price then stay aligned with their `<th>`s and only Total
 * Price's right edge can pull in.
 */
export const orderItemGridTemplate = (widths = null) => {
  const block = orderItemBlockWidth(widths);
  return ITEM_COLUMNS
    .map((c, i) => {
      const pct = ITEM_TRACK_PCT(widths, c, block);
      return i === ITEM_COLUMNS.length - 1 ? `minmax(0, ${pct})` : pct;
    })
    .join(' ');
};

/**
 * The table's LIVE floor — the sum of the user's CURRENT widths.
 *
 * 🚨 **THIS IS WHAT THE `<table>`'s `minWidth` MUST BE, AND MISTAKING IT FOR THE
 * DEFAULTS' SUM IS THE MOST LIKELY WAY TO BREAK THE DRAG.** With the
 * `<colgroup>` carrying live widths and `minWidth` frozen at the defaults' sum,
 * a table whose scrollport is narrower than that sum is pinned at the OLD total
 * while fixed layout scales the new declared widths to fit it — so the user
 * drags a column wider and **every column gets thinner while the table never
 * grows**. Same fix, same reason, as `GagGroupTable`'s `totalWidth`.
 */
export const orderTableTotalWidth = (widths = null, columns = ORDER_TABLE_COLUMNS) =>
  columns.reduce((sum, c) => sum + orderColumnWidth(widths, c), 0);

/**
 * The floor **at the DEFAULT widths** — a `reduce`, never a literal.
 *
 * 🔀 **SINCE DRAG-TO-RESIZE THIS IS NO LONGER THE NUMBER THE TABLE RENDERS AT.**
 * It survives as the value a reset returns to and as the first-paint fallback
 * for a shell rendered without the hook. The live floor is
 * `orderTableTotalWidth(widths)` above.
 *
 * 🔢 **DO NOT QUOTE A PIXEL FIGURE FOR IT ANYWHERE.** It moved five times on
 * 2026-08-08 alone and every derived number quoted in prose went stale with it
 * — including CLAUDE.md's own, which said **1510** until 2026-08-10 while the
 * tree computed **1520** (the chain stopped before Date went 90 → 100 for the
 * two-line cell). Import the constant.
 */
export const ORDER_TABLE_MIN_WIDTH = ORDER_TABLE_COLUMNS.reduce((sum, c) => sum + c.width, 0);

/**
 * MY ORDERS' COLUMN MODEL — the twelve MINUS `Ordered By` (2026-08-12).
 *
 * Product owner: *"on my orders we can drop the ordered by column as you said.
 * Just look out for unintended consequences. if any"*.
 *
 * 🔑 **Every order on that page is the viewer's own** — `GET /api/orders?user_id=`
 * filters on the AUTHENTICATED id, never the query param — so the cell rendered
 * one constant string on every row. It was ~150px of a 1520px table
 * (1500 since the 2026-09-13 width retune — the ratio is the point, not the
 * figure; import `ORDER_TABLE_MIN_WIDTH`)
 * spent on the answer to a question nobody asked.
 *
 * 🚨 **DERIVED BY FILTER, NEVER RETYPED.** A hand-written eleven-entry copy is a
 * second column model: the day someone retunes a width, adds a cycle state or
 * changes a `min` in `ORDER_TABLE_COLUMNS`, this one silently keeps the old
 * value and the two surfaces drift — which is the whole failure this file's
 * `ITEM_GRID` docblock is about, one level up. The filter also guarantees every
 * column keeps its `min`, without which `useColumnWidths`'s floor is silently
 * ZERO and a heading drags to nothing.
 *
 * 🟢 **THE ITEM BLOCK IS UNTOUCHED, WHICH IS WHY THIS IS NOT A CONTRACT CHANGE.**
 * `ITEM_COLUMN_KEYS` is item/quantity/unit_price/total_price and `ordered_by` is
 * none of them, so `ITEM_BLOCK_WIDTH` and all four `ITEM_GRID` proportions —
 * "the one deliberate literal in that file", pinned by §6 of
 * `OrderTableView.itemPreview.test.js` — are bit-for-bit unchanged, and that
 * suite passes with no edit. **If a future column change DOES move them, stop:
 * that is its own commit with its own reasoning.**
 */
export const MY_ORDERS_TABLE_COLUMNS = Object.freeze(
  ORDER_TABLE_COLUMNS.filter((c) => c.key !== 'ordered_by')
);

/**
 * ── THE MY ORDERS PHONE TABLE (below `md`, 2026-09-30) ───────────────────
 * Owner: *"Mobile order table becomes the default on My Orders … columns
 * PO/Order · Job with the ORDER DATE … on a small grey second
 * line · Supplier · Item (first item, "+N more") · Total · Status. Narrow
 * mobile default widths with its own width key; truncate with "…"; tap
 * targets >= 44px; tapping a row still expands it."*
 *
 * 🔑 **DERIVED FROM `ORDER_TABLE_COLUMNS`, NEVER RETYPED** — each entry is the
 * shared column (its key, sort field, cycle states and `min`) with ONLY the
 * phone overrides below spread over it. `item` keeps its key but is ONE cell
 * here (the first item + "+N more"), not the desktop's four-column item block,
 * so `ITEM_GRID` / `ITEM_BLOCK_WIDTH` are untouched and never read by this row.
 * No `expand` column: the chevron rides inside the PO cell, which buys
 * back 40px on a 360px screen; the whole row is still the tap target.
 * 🔻 **NOTHING IS FROZEN (2026-09-30, owner, tested on a real Android phone):**
 * the PO/Order column scrolls with the rest. It shipped `frozen: true` the same
 * day; do not restore it. `ui/PageScrollTable` keeps the opt-in (no caller).
 * 🔑 `job_name` CYCLES `Job → Date`, because its cell shows both — the same
 * "every state names something in the cell" rule the desktop Date column uses.
 * ⚠️ Widths are ESTIMATES sized for "ORD-1736" + a marker, a truncated job
 * name, and "£12,345.67"; not browser-measured. Column resize is off below
 * `md` anyway (`ui/PageScrollTable`'s `NARROW_COLUMN_RESIZE_QUERY`).
 */
/* 🔀 2026-10-01 (owner, phone): *"Job column header reads "JOB" at rest (date
   is its small grey second line)"* and *"narrower PO/ORD and Date columns"*.
   The column still CYCLES Job → Date as a SORT, but its VISIBLE heading is
   "Job" in both states — the date is the cell's second line, not a column of
   its own — and the date state carries its name for assistive tech (sr-only)
   and for the tooltip (`sortLabel`). The page opens on `order_date`, so this
   is the "at rest" heading. Widths were 104 / 116. 📏 Measured in Chromium at
   360/375/412: the PO cell's tighter padding (`pl-1 pr-0.5`, `gap-0.5`, the
   clip at `ml-0.5`) leaves the ORD line the SAME 68px of content room the old
   104px column had, so "ORD-999" + paperclip (67px) still fits and nothing
   truncates that did not before ("ORD-1736" + clip, 73px, did and does). */
export const MY_ORDERS_MOBILE_JOB_DATE_LABEL = (
  <>Job<span className="sr-only"> (sorted by date)</span></>
);
const MY_ORDERS_MOBILE_OVERRIDES = Object.freeze({
  // 🔀 2026-10-03: ONE phone PO width everywhere (was 92/84 here).
  po_number: PHONE_PO_ORDER_COLUMN,
  job_name: {
    // 102 (owner 2026-11-01): "No job name" is 83.73px (Inter 14px, measured),
    // so 102 - 8 (tightened) - 8 (padding) = 86 fits it; was 96.
    width: 102,
    min: 76,
    label: 'Job',
    fields: [
      { sortField: 'job_rig', label: 'Job', direction: 'asc' },
      {
        sortField: 'order_date',
        label: MY_ORDERS_MOBILE_JOB_DATE_LABEL,
        sortLabel: 'Raised', // owner 2026-11-03: the same word as the desktop heading
        description: 'Sorted by the date the order was raised (the grey second line).',
        direction: 'desc',
      },
    ],
  },
  // 📱 Owner 2026-10-03: "po, job, item, supplier…" — Item before Supplier,
  // the same order as Dept Orders and the hires phone tables.
  item: { width: 136, min: 90 },
  supplier: { width: 104, min: 80 },
  order_total: { width: 92, min: 80, label: 'Total' },
  status: { width: 108, min: 90 },
});

/**
 * THE phone column builder (2026-10-01, generalised when Department Orders and
 * To Approve took the phone table too). An ordered map of `key → overrides`;
 * each entry is the SHARED column from `ORDER_TABLE_COLUMNS` with only the
 * overrides spread over it, so no phone set can retype a sort field or drop a
 * `min`. Object key order IS the column order.
 */
const buildPhoneOrderColumns = (overrides) => Object.freeze(tightenPhoneColumns(
  Object.keys(overrides).map((key) => {
    const base = ORDER_TABLE_COLUMNS.find((c) => c.key === key);
    return Object.freeze({ ...base, ...overrides[key] });
  })
));

export const MY_ORDERS_MOBILE_TABLE_COLUMNS = buildPhoneOrderColumns(MY_ORDERS_MOBILE_OVERRIDES);

/**
 * Its OWN width key — a different column set on the same `useColumnWidths`
 * would corrupt `sfx_my_orders_col_widths` on the first write (the rule at
 * `MY_ORDERS_COL_WIDTHS_KEY`). 🪤 Passed as a CONSTANT: grep
 * `sfx_my_orders_mobile_col_widths` by hand, `check-bloat` cannot see it.
 */
export const MY_ORDERS_MOBILE_COL_WIDTHS_KEY = 'sfx_my_orders_mobile_col_widths';

/**
 * ── THE DEPARTMENT ORDERS + TO APPROVE PHONE TABLE (below `md`, 2026-10-01) ──
 * Owner: *"Dept Orders + To Approve: phone TABLE instead of cards"*, columns
 * PO/Order · Job (with the NEEDED-BY date beneath it + the lead-time dot) ·
 * Ordered by (first name + surname initial) · Item · Total · Supplier ·
 * Status (last, may sit off-screen). To Approve is the same MINUS Status —
 * every row there is `submitted`, so the cell would say one thing on every row.
 *
 * 🔑 Same builder and the same row (`OrderMobileTableRow`) as My Orders; what
 * differs is DATA on the column: `jobDate: 'required_by'` puts the desktop
 * Date cell's second line (needed-by + `LeadTimeDot`, same fields, same
 * `showsTimeCriticalChrome` gate, same `buildOrderDateTooltip`) under the job
 * instead of the order date. The PO cell is `PhonePoOrderCell` (both
 * markers on every phone table since 2026-10-03).
 * 🔑 Job CYCLES `Job → Needed by` (`required_by_date`, the desktop Date
 * column's `Due` state) — the My Orders two-field shape, mirrored.
 * 📏 Widths: measured in Chromium (CI=true build) at 360/375/412, light and
 * dark — no cell spills, no heading clips, the page never scrolls sideways.
 */
export const PHONE_JOB_NEEDED_BY_LABEL = (
  <>Job<span className="sr-only"> (sorted by needed-by date)</span></>
);
const DEPT_PHONE_OVERRIDES = Object.freeze({
  po_number: PHONE_PO_ORDER_COLUMN,
  job_name: {
    width: 104,
    min: 80,
    label: 'Job',
    jobDate: 'required_by',
    fields: [
      { sortField: 'job_rig', label: 'Job', direction: 'asc' },
      {
        sortField: 'required_by_date',
        label: PHONE_JOB_NEEDED_BY_LABEL,
        sortLabel: 'Needed by',
        description: 'Sorted by the needed-by date (the grey second line).',
        direction: 'asc',
      },
    ],
  },
  // 120: the 'Ordered By' heading measures 118px of cell in Chromium (86.02
  // label + gap + caret + px-2) — the same floor as the desktop column.
  ordered_by: { width: 120, min: 120 },
  item: { width: 136, min: 90 },
  supplier: { width: 104, min: 80 },
  order_total: { width: 92, min: 80, label: 'Total' },
  status: { width: 108, min: 90 },
});
export const DEPT_ORDERS_MOBILE_TABLE_COLUMNS = buildPhoneOrderColumns(DEPT_PHONE_OVERRIDES);
// To Approve: the same set MINUS Status (every row is awaiting approval).
export const APPROVALS_MOBILE_TABLE_COLUMNS = Object.freeze(
  DEPT_ORDERS_MOBILE_TABLE_COLUMNS.filter((c) => c.key !== 'status')
);
// Their OWN width keys — a different column set must never share a key (the
// rule at `MY_ORDERS_COL_WIDTHS_KEY`). 🪤 Constants: grep them by hand.
export const DEPT_ORDERS_MOBILE_COL_WIDTHS_KEY = 'sfx_dept_orders_mobile_col_widths';
export const APPROVALS_MOBILE_COL_WIDTHS_KEY = 'sfx_approvals_mobile_col_widths';

// 🔻 THE ALL-MODE "Department" COLUMN WAS REMOVED ON 2026-08-31 with the All
// Productions mode. It lived here as `ALL_MODE_DEPARTMENT_COLUMN`,
// `withAllModeDepartmentColumn(columns, isAllDepartments)` and
// `orderDepartmentName(assignments, departmentId)`, plus an
// `OrderDepartmentAssignmentsContext` threading the caller's own assignments
// from the shell to every row. Its whole purpose was that in All mode the
// table showed rows from several departments at once and nothing else on a
// row said which; with one department in view at a time, every row on the
// table belongs to the department the user is standing in, so the column
// would say the same thing on every row.
//
// 🔑 This restores the sanctioned-exception column set exactly
// (`client/src/pages/CLAUDE.md`, the verbatim-spreadsheet box), which the
// column was a declared departure from. `OrderTableView.columns.test.js` §1
// pins the twelve headings and cells against it.

/**
 * The measured width of the scrollport a row is sitting in, or `null` before
 * the first measurement / when a row is rendered outside `OrderTableShell`.
 *
 * It exists only to size the expanded row's sticky panel (see the comment at
 * its use site). A context rather than a prop because the pages build their
 * rows themselves and pass them to the shell as `children` — the shell cannot
 * inject a prop into a node it did not create, and threading it through both
 * pages would put a layout detail of THIS component into two files that have
 * no other reason to know about it.
 *
 * 🔑 It carries a measurement and nothing else. Never hang anything
 * role-shaped off it: CLAUDE.md's sanctioned-exception box requires that this
 * component never re-derive a permission.
 */
const OrderTableWidthContext = createContext(null);

/**
 * The LIVE column-width map, or `null` when the shell was given none.
 *
 * 🔑 **A SECOND CONTEXT, deliberately NOT bundled into `OrderTableWidthContext`
 * above.** That one's docblock says in terms that it *"carries a measurement and
 * nothing else"*, and every existing consumer of it should stay byte-identical.
 * The two also answer different questions — one is a measured scrollport, this
 * is a declared width model — and merging them would make each consumer
 * re-render when the other's value moved.
 *
 * 🔑 **It has to be a context and not a prop for the same reason that one does:
 * the PAGES build the rows themselves and hand them to the shell as
 * `children`, so the shell cannot inject a prop into a node it did not
 * create.** Threading it through both pages instead would put a layout detail
 * of THIS component into two files that have no other reason to know about it.
 *
 * `null` means "no hook is driving this table", and every reader falls back to
 * the measured defaults — which is what keeps a bare shell, a row rendered
 * outside one, and every existing test byte-identical.
 */
const OrderColumnWidthsContext = createContext(null);

/**
 * WHICH COLUMNS THIS TABLE RENDERS — a THIRD context, added 2026-08-12.
 *
 * 🔑 **It defaults to `ORDER_TABLE_COLUMNS`, which is what makes every existing
 * caller byte-identical.** A row rendered outside a shell (several tests do
 * exactly that) reads the default and behaves precisely as it did before this
 * existed; `DepartmentOrders.js` and `Approvals.js` pass no `columns` and so
 * provide the default too. **That byte-identity is the property this change
 * stands on — it is asserted, not assumed** (`OrderTableView.columns.test.js`
 * §1 compares the rendered heading and cell sequences against the shipped
 * twelve).
 *
 * 🚨 **A CONTEXT AND NOT A PROP, FOR THE REASON THE OTHER TWO RECORD: the rows
 * are nodes the PAGE built, so the shell cannot inject anything into them.**
 * Threading `columns` through every `<OrderTableRow>` at three call sites would
 * make the row's column model a thing three pages have to remember to keep in
 * step with the shell's — i.e. exactly the desync this whole change exists to
 * make impossible.
 *
 * 🔴 **Do NOT bundle it into either of the other two.** `OrderTableWidthContext`
 * carries a measured scrollport, `OrderColumnWidthsContext` carries the live
 * width map, this carries the column MODEL. Three questions; a combined value
 * re-renders every consumer when any one of them moves.
 */
const OrderColumnsContext = createContext(ORDER_TABLE_COLUMNS);

/**
 * The collapsed preview box, as a FIXED height rather than a cap.
 *
 * ── Where 3.25rem comes from, since a magic number here would be one ──
 * It is not new: this component already used `max-h-[3.25rem]`, and the value
 * is derived from the line it has to show: 3.25rem (52px) is TWO preview lines
 * plus a sliver of the third. That sliver is the affordance the product owner
 * needs — a clipped row edge says "there is more" even where the OS hides
 * overlay scrollbars until you touch them.
 *
 * ✅ RE-DERIVED 2026-08-12, AND THE ORIGINAL 2026-08-07 SUM IS NOW THE CORRECT
 * ONE AGAIN — the line IS `py-0.5` + `text-sm` = 0.125 + 1.25 + 0.125 = 1.5rem
 * = **24px**, measured. Two lines are 48px inside this 52px box, so the sliver
 * of the third is 4px. The value stays at 3.25rem and now delivers exactly what
 * this docblock has always claimed for it.
 *
 * ⚠️ It did NOT deliver that between 2026-08-07 and 2026-08-12, and the history
 * is worth keeping because it is the reason to distrust a derived figure here.
 * The item spans carried no text-size class at all, so they inherited the 16px
 * document default; the docblock was corrected to say so and to quote a
 * measured line box of **25.33px**. 🚨 **That measurement was itself wrong.**
 * Tailwind's preflight sets `html { line-height: 1.5 }`, so a 16px span is a
 * 24px line box plus 4px of `py-0.5` = **28px** — re-measured in Chromium at
 * 1440px, `getBoundingClientRect().height` on the grid row, and 28.00 exactly.
 * At 28px a 52px box shows **1.857 lines**, not two plus a sliver: the
 * affordance this constant exists to provide was quietly absent on every
 * multi-item row, and the docblock's own corrected arithmetic (50.66 + 1.34)
 * concealed it by landing close enough to two lines to look right.
 * 🕶️ THE LESSON, WHICH IS NOT ABOUT LINE HEIGHTS: a docblock that records a
 * measurement is only as good as the measurement, and a WRONG one is worse than
 * an absent one — it was the corrected figure that made this look already
 * investigated. Re-measure before you trust a number in a comment, including
 * one that says it was measured.
 *
 * The 16px is gone as of 2026-08-12 (see the `text-sm` on the grid row and the
 * reasoning at `TD_ORDER_TOTAL`), which is what puts the arithmetic back.
 *
 * 🔑 That sliver was NOT being delivered before 2026-08-07, on any multi-item
 * row: the preview had a horizontal scrollbar (see ITEM_GRID above), which ate
 * 15px of this 52px box and left clientHeight at 37px — barely over ONE line.
 * Removing that scrollbar is what restored it. Do not "fix" the height here to
 * chase a symptom whose cause is track sizing.
 *
 * ── Why `h-`, not `max-h-` ───────────────────────────────────────────
 * With a cap, an order of two items (3rem of content) rendered SHORTER than
 * one of three, so row heights stepped up and down the table and only 3-item
 * rows ever scrolled. A fixed height makes every row with items identical
 * whatever the order contains, which is the half of *"always scrollable …
 * when number of items > 1"* that "scrollable" was standing in for. Long
 * orders were already prevented from stretching the row by the old cap; that
 * property is kept, not introduced.
 */
const PREVIEW_BOX_HEIGHT = 'h-[3.25rem]';

/**
 * The bottom-edge fade that says "there is more below".
 *
 * Product owner, 2026-08-07: *"any chance we can fade out the scroll lists when
 * collapsed to signify that the list is scrollable? maybe 50% gradient fade on
 * the last item line?"*
 *
 * A plain CSS class, defined once in `index.css` — NOT a Tailwind utility. The
 * full reasoning (why a CSS mask rather than an overlay gradient, why a named
 * class on Tailwind 3.4, and where the 65% / alpha-0.5 stops come from) is in
 * the block comment above the rule there, next to the values it explains. The
 * short version: a mask fades whatever is actually behind the box — default,
 * hover, expanded tint — where an absolutely-positioned gradient div would have
 * to pick ONE of those backgrounds and be wrong in the others, and it cannot
 * swallow a pointer event, so click-to-expand and the drag guard below are
 * untouched. (This list named a fourth, the green "actionable" row outline,
 * until 2026-08-08; that outline is deleted — the signal is the chevron now —
 * and the mask's advantage is unchanged by its going.)
 *
 * 🔀 Applied ONLY when `faded` (`total > 2`) — owner ruling 2026-10-28: *"if
 * there are only one or two items dont fade the bottom of the second item. If
 * there are MORE than 2 items keep the fade to signify the scroll"*. Two items
 * FIT the box, so fading the second one hides content that is all there.
 * The box still SCROLLS from 2 items (`scrollable`, `total > 1`) — harmless
 * when nothing overflows. `faded` implies `scrollable`, so a row can never fade
 * without scrolling; the reverse (2 items: scroll container, no fade) is the
 * ruling, not drift.
 */
const PREVIEW_SCROLL_FADE = 'scroll-fade-bottom';

/**
 * 🐛→✅ **THE VERTICAL SIBLING OF THE 2026-08-07 HORIZONTAL-SCROLLBAR BUG,
 * FOUND 2026-08-16.** Owner: *"quanity for table view column dont seem
 * alignedverticaly for all collapsed orders"*.
 *
 * `ItemPreview`'s box is `overflow-y-auto` and is ALSO the grid container
 * whose four `ITEM_GRID` tracks are PERCENTAGES of its own content-box width
 * (see that docblock). A row only grows a REAL vertical scrollbar once its
 * items overflow the fixed 52px box (`PREVIEW_BOX_HEIGHT`) — one item never
 * does, two items usually FIT (no scrollbar even though `scrollable` is
 * true), three or more reliably overflow. So only rows that are ACTUALLY
 * scrolling right now lose client width to the app's own thin scrollbar
 * (`index.css`'s `::-webkit-scrollbar`, measured 10px), and the percentage
 * tracks correctly resolve against that narrower box on exactly those rows —
 * shifting Item/Quantity/Unit Price/Total Price a few px LEFT of every other
 * row's. Measured in a HEADED (not headless) Chromium against the compiled
 * stylesheet: headless renders overlay scrollbars unconditionally (a forced
 * `overflow-y:scroll` box there loses ZERO client width), which would have
 * hidden this bug from any headless measurement entirely.
 *
 * 🚫 **`scrollbar-gutter: stable` on every row was tried and rejected.** It
 * does make every row agree with every OTHER row, but only by making ALL of
 * them sit a few px left of their OWN headers, permanently — not just the
 * scrolling ones. `OrderTableView.js` aligns to its headers to within
 * **0.00px** after a column drag (see the sanctioned-exception box in
 * CLAUDE.md); that cannot survive a fixed-pixel gutter reservation once the
 * table also stretches or gets dragged, because the reserved amount does not
 * scale with the live block width while the declared column widths do — no
 * single static percentage reconciles the two at every table width. Only a
 * scrollbar that reserves NO width keeps 0.00px true everywhere, which is
 * what this class does instead of compensating for a nonzero one.
 *
 * The scroll itself is untouched: `scrollbar-width: none` and a hidden
 * `::-webkit-scrollbar` remove only the drawn thumb/track. The box is still
 * `overflow-y-auto`, `scrollTop` still moves on wheel/keyboard/programmatic
 * scroll, and the wheel clamp and the drag-vs-click guard below — both of
 * which read `scrollTop`, never the scrollbar's presence — are unchanged.
 * "There is more" was never carried by the scrollbar alone: the sliver
 * (`PREVIEW_BOX_HEIGHT`) and `PREVIEW_SCROLL_FADE` already say it.
 *
 * Scoped to this box only, in `index.css`, beside the app-wide thin-scrollbar
 * theme it deliberately does not touch — the expanded panel, dropdown menus
 * and the table's own horizontal scrollport keep the visible thumb. Applied
 * only when `scrollable`, the same predicate that decides whether the box is
 * a scroll container at all: a single-item row has no scrollbar to hide.
 */
const PREVIEW_NO_NATIVE_SCROLLBAR = 'order-item-preview-scrollbar';

/**
 * The three sort-caret glyphs, and the box they all share.
 *
 * Product owner, 2026-08-08, in two messages — the second superseding the first:
 * *"hovering over a table header should show the filter up/down arrow in a light
 * grey to show there is a control there"*, then *"maybe even show the light faded
 * grey arrow all the time and a blue for the current selected order type."*
 *
 * **The always-visible option was taken, not the hover-only one.** Hover-only
 * would have been free of reflow here (the box was already reserved), but
 * always-visible is strictly better: the affordance is discoverable without
 * hunting for it, and it makes the sortable/non-sortable split visible at a
 * glance — which is exactly what the four line-item columns depend on, since
 * they are deliberately not sortable and must not look as though they are.
 *
 * 🔑 **`SORT_CARET_NONE` is a NEUTRAL up-down arrow, not a faded `▲`.** A faded
 * ascending triangle asserts a direction that is not in effect; `↕` says "this
 * column sorts, and it is not the current sort" — the convention GitHub and Jira
 * both use.
 *
 * ── Why the box is `w-3` (12px) and not the `w-2.5` (10px) it was ─────
 * MEASURED in Chromium at `text-xs` in the production font (Inter 600), against
 * the compiled stylesheet:
 *   `↕`  6.61px
 *   `▲`  10.94px      ← and `▼` the same
 * So **the ACTIVE caret was already overflowing its 10px box by 0.94px** — a
 * pre-existing 2026-08-08 error, mine, from the commit that added these headers:
 * `w-2.5 shrink-0` claims to reserve enough room and did not. 12px fits the
 * widest glyph with 1.06px to spare. `text-center` keeps the narrow `↕` sitting
 * where the wide `▲` sits, so switching columns does not shuffle the glyph
 * sideways inside its own box.
 *
 * ⚠️ Widening the box raises every sortable header's requirement by 2px, which
 * is pinned in §6 of `OrderTableView.sortableHeaders.test.js` — those figures
 * were re-measured with this box and must be re-measured again if it changes.
 */
/**
 * 🔀 **THE CARET DEFINITIONS AND THEIR REASONING MOVED TO
 * `components/ui/SortableHeader.js` ON 2026-08-08 — everything from here down to
 * `TH_BASE` is now a RE-EXPORT, not a definition.** Product owner: *"make the
 * table view layout a single piece of code or class that can be used on all
 * table pages so we dont diverge ever again"*. The long docblocks above and
 * below this point were moved verbatim with the code they explain; they are not
 * repeated here, because two copies of a measurement is exactly the drift being
 * fixed.
 *
 * 🔑 **They are RE-EXPORTED rather than deleted because this module is the
 * public face of the order table**: `OrderTableView.sortableHeaders.test.js` and
 * any future consumer import the glyphs from here, and pointing them at a `ui/`
 * module they otherwise have no business knowing about would be churn for its
 * own sake. The single definition lives in one place; this is an alias.
 */
export { SORT_CARET_ASC, SORT_CARET_DESC, SORT_CARET_NONE } from './ui/SortableHeader';

/**
 * The ACTIVE caret: blue, full strength. Unchanged by the 2026-08-08 always-on
 * change — *"a blue for the current selected order type"* is what it already did.
 * Measured contrast against the header background (`bg-gray-50`, and `#1e293b` in
 * dark — see the inactive constant for why the dark value is not gray-700/50):
 * **3.52:1 light, 5.75:1 dark**, so the one indicator that carries STATE clears
 * WCAG 1.4.11's 3:1 in both themes.
 */
// 🔀 MOVED to `components/ui/SortableHeader.js` (2026-08-08) — the docblock
// above travelled with it and is kept there beside the code. Not re-exported:
// nothing outside the header cell has ever used it.

/**
 * The INACTIVE caret: *"light faded grey"*, his words, plus a hover step.
 *
 * 🚨 **THE HOVER STEP IS `opacity`, NOT A SECOND COLOUR, AND THAT IS A CASCADE
 * DECISION MEASURED RATHER THAN A STYLE PREFERENCE.** `client/src/index.css`
 * carries a table of global dark-mode overrides — `html.dark .text-gray-400 {
 * color: #6b7280 !important }` and siblings for 300/500/600/700/800/900 — and an
 * `!important` author declaration beats ANY non-important rule regardless of
 * specificity. So a `dark:group-hover:text-gray-300` is **inert**.
 *
 * Measured, with the candidate rules injected so the test was about the cascade
 * and not about whether Tailwind had compiled them:
 *   colour-based hover — light 2.43 → 4.63 ✅, **dark 3.03 → 3.03 ❌ (nothing)**
 *   opacity-based hover — light 1.80 → 2.43 ✅, dark 2.18 → 3.03 ✅
 * A colour hover would have worked on the maintainer's screen and silently done
 * nothing in dark mode. This is the same shape as the bug that ate the green
 * actionable chevron hours earlier, arriving through a different rule — **check
 * the cascade of any colour that carries meaning, not only its contrast.**
 *
 * 🪤 The same override table means **the `dark:` variant here is delivered by
 * `index.css`, not by Tailwind**: `html.dark .text-gray-400` forces `#6b7280`,
 * which is what `dark:text-gray-500` asks for anyway. The variant is kept
 * precisely so the declared intent and the enforced colour AGREE — remove it and
 * the element still renders `#6b7280` today, but for a reason nobody wrote down.
 *
 * ── Contrast, against the header background it actually sits on ───────
 * (Light `#f9fafb`; dark `#1e293b`, because `html.dark .bg-gray-50` overrides the
 * `dark:bg-gray-700/50` on the `<thead>` — measured, not assumed.)
 *   rest   **1.80:1** light · **2.18:1** dark
 *   hover  **2.43:1** light · **3.03:1** dark
 * Deliberately below the label (4.63 light / 5.76 dark) so it cannot compete with
 * it, and deliberately faint at rest because *"light faded grey"* is the request.
 * ♿ It is a REDUNDANT affordance, which is what makes those figures acceptable:
 * the control's identity is carried by a real `<button>` with a focus ring, a
 * `title="Sort by …"`, and `aria-sort` — the caret adds a visual hint on top. The
 * indicator that carries STATE is the blue one above, and that clears 3:1.
 * **`opacity-70` is the dial** if he wants it stronger.
 */
// 🔀 MOVED to `components/ui/SortableHeader.js` (2026-08-08), with the cascade
// measurement above it. Not re-exported for the same reason as the active one.

/**
 * The part of a body cell that is about BOXES, not about type: padding and
 * vertical alignment. Split out of `TD` on 2026-08-12 so `TD_ORDER_TOTAL` can
 * differ in its type WITHOUT inheriting a second, conflicting font-size and a
 * second, conflicting colour — see the trap note there.
 */
const TD_LAYOUT = 'px-2 py-2 align-middle';
const TD = `${TD_LAYOUT} text-sm text-gray-700 dark:text-gray-300`;

/**
 * The Order Total cell — `TD` with the SIZE exchanged for `text-base`.
 *
 * ── Why this cell is one step larger than every other cell in the row ──
 * Product owner, 2026-08-12: *"on the order table the TOTAL price is bolder and
 * larger than the Order total. Total price is the quantity x unit price but
 * order total should be more prominent I think?"*
 *
 * He was right about the INVERSION and wrong about which dimension carried it,
 * and the distinction is why this is a two-line change rather than a redesign.
 * Measured in Chromium at 1440px before the fix:
 *
 *   line-item Total Price   16px / weight 500 / rgb(31,41,55)
 *   Order Total (this cell) 14px / weight 600 / rgb(31,41,55)
 *
 * Colour was already identical and the PARENT was already the heavier of the
 * two — so "bolder" was not the defect. What was inverted is SIZE, and only
 * size: the item-preview spans carry no text-size class at all, so the nested
 * block rendered at the 16px document default while every cell CONTAINING it
 * rendered at `text-sm`. **That 16px was never chosen** — see `ItemPreview`'s
 * line-box note below, where the same accident is already recorded twice as
 * archaeology.
 *
 * 🔑 So the two treatments are exchanged in the ONE dimension that was wrong:
 * the preview drops to the table's own `text-sm`, this cell takes the 16px the
 * preview was using. The weights are untouched because they were already
 * right, and a literal swap of BOTH dimensions would have fixed the size
 * inversion while creating a weight one — the same defect in the other
 * dimension. No third weight and no new size enters the type scale.
 *
 * 🟢 THE RESULT IS NOT A NEW TREATMENT: `text-base font-semibold text-gray-800
 * dark:text-gray-200` is byte-for-byte what the retired card layout used for
 * this same figure (measured 16px / 600 / rgb(31,41,55)).
 *
 * 🪤 IT BUILDS ON `TD_LAYOUT`, NOT ON `TD`, AND THAT IS THE WHOLE REASON THE
 * SPLIT EXISTS. Two font-size utilities on one element resolve by STYLESHEET
 * order, not by the order they are written — the same hazard CLAUDE.md records
 * for `mb-3 mb-2` on `ui/Toolbar`. Tailwind happens to emit the size scale in
 * ascending order, so `${TD} text-base` would work today and would silently
 * invert the day that changes.
 * 🔎 **And the colour was ALREADY in that state before this change, which is
 * how the hazard was found rather than reasoned about.** This cell has read
 * `${TD} … text-gray-800 dark:text-gray-200` for as long as it has existed, so
 * it carried FOUR colour utilities — `text-gray-700 dark:text-gray-300` from
 * `TD` and the pair beside them — and rendered gray-800 only because Tailwind
 * emits `-700` before `-800`. Correct output, resting on stylesheet order, with
 * nothing on screen to show for it; a `§10.4` token comparison against the card
 * layout is what surfaced it. **Both are now declared exactly once.**
 * Padding and vertical alignment still come from the shared constant, so this
 * cell differs from its neighbours in type and in nothing else.
 */
const TD_ORDER_TOTAL = `${TD_LAYOUT} text-right text-base font-semibold text-gray-800 dark:text-gray-200`;
// 🔀 `TH_BASE` MOVED to `components/ui/SortableHeader.js` (2026-08-08) and is
// NOT imported back: both tables carried the identical string, and now that the
// header cell is a shared component nothing in THIS file styles a `<th>` any
// more. (It is not used by the item-preview grid either — that aligns off
// `ITEM_GRID`, which derives from `ORDER_TABLE_COLUMNS`, not off the heading
// type treatment.) The `CI=true` build is what proves the binding is really
// unused: it turns an unused import into an error.

/**
 * The line items a collapsed row can show WITHOUT issuing a request.
 *
 * THE ADAPTER. Sources are tried in order and every one of them is already
 * paid for, so this never fetches:
 *
 *  1. `order.preview_items` — the list-payload field: description / part
 *     number / quantity / unit price / line total / is_hire and NOTHING ELSE.
 *     🔑 It must never carry `cost_code`, `payment_type_name` or
 *     `budget_pot_id`: those are gated per order by `canViewOrderAccounting`
 *     in the order's OWN department, and an ungated array sitting beside a
 *     gated summary field is exactly how the 2026-08-05 accounting leak got
 *     in. Pinned here by `OrderTableView.previewItems.test.js` and, at the
 *     source, by `server/helpers/orderPreview.js`'s allowlist.
 *  2. `detail.items` — already in memory once a row has been expanded, so a
 *     collapsed row that was opened earlier previews the real thing.
 *  3. `order.first_item_description` — a single degraded row (description
 *     only, no numbers) for any list payload that carries no `preview_items`.
 *  4. nothing.
 *
 * Switching the source is an edit HERE and nowhere else.
 *
 * ── ⚠️ CONTRACT CHANGE 2026-08-07: NO TRUNCATION. Both branches used to end
 * `.slice(0, ORDER_ITEM_PREVIEW_LIMIT)` with the limit at 3, and that
 * constant is now DELETED rather than raised. Product owner: *"the card then
 * allows scrolling of the items but capped at 3 by the looks of things.
 * Please make them all scrolling."* The box is a fixed `h-[3.25rem]` scroll
 * area, so "all of them" costs no height — it is the SCROLL EXTENT that
 * grows, which is the whole request.
 *
 * A limit here would also now be actively wrong rather than merely stingy:
 * with the server sending every item, a client-side cap re-creates the
 * "+N more" state the owner was complaining about, from data already on the
 * wire. If a future payload concern brings a cap back, cap it SERVER-side
 * where it saves bytes — capping it here saves nothing.
 *
 * Source (3) is the one place a preview can still be shorter than the order,
 * so "+N more" below is NOT dead code. It is unreachable from the two pages
 * using this table today (both list endpoints send `preview_items`), and
 * reachable through this adapter by any list endpoint that does not.
 */
export function orderPreviewItems(order, detail) {
  if (Array.isArray(order?.preview_items) && order.preview_items.length > 0) {
    return order.preview_items;
  }
  if (Array.isArray(detail?.items) && detail.items.length > 0) {
    return detail.items;
  }
  if (order?.first_item_description) {
    return [{ id: `first-${order.id}`, description: order.first_item_description, partial: true }];
  }
  return [];
}

/**
 * How many items the order really has — which is NOT the preview length, and
 * is what drives "+N more". `item_count` is computed server-side on
 * `GET /api/orders/department` already.
 */
export function orderItemCount(order, detail) {
  if (Number.isFinite(order?.item_count)) return order.item_count;
  if (Array.isArray(detail?.items)) return detail.items.length;
  if (Array.isArray(order?.preview_items)) return order.preview_items.length;
  return order?.first_item_description ? 1 : 0;
}

// 🔀 `ExpandChevron` (and its docblock) moved VERBATIM to
// `components/TableRowExpansion.js` on 2026-09-27 — the hires Table shares it.

/** How far the pointer may travel between press and release and still be a click. */
const DRAG_SLOP = 4;

/**
 * How many items ONE mouse-wheel notch may advance the collapsed preview.
 *
 * Product owner, 2026-08-08: *"scroll wheel scrolls too much on the table view
 * on department orders and to approve when scrolling through multiple items on
 * the collapsed table view. can we make it scroll only 1 item at a time, maybe
 * 2 tops"*. A Chrome notch is ~100px and an item line is ~25px, so one notch
 * jumped roughly four items in a box that shows two — the list flew past.
 *
 * This is the "maybe 2 tops" dial and the only number to change if he wants it
 * looser. It is a CEILING, not a step size — see the clamp below.
 */
const WHEEL_MAX_ITEMS_PER_NOTCH = 1;

/**
 * Item height to assume when the rendered row cannot be measured.
 *
 * 🔑 The height is MEASURED per event, not computed, and this is only the
 * fallback for a box whose first line reports no height (jsdom, a hidden
 * ancestor, a first paint before layout).
 *
 * 🔀 **24 SINCE 2026-08-12, WAS 26 — and it MUST move with the line, which is
 * the whole hazard of a fallback constant derived from a rendered size.** The
 * grid row now carries `text-sm` (see the comment on it, and the reasoning at
 * `TD_ORDER_TOTAL`), so the line box is `py-0.5` + a 20px `text-sm` line
 * = **24px**, re-measured in Chromium rather than re-derived. Left at 26 this
 * would over-scroll by 2px per notch on exactly the surfaces that cannot
 * measure — a drift nothing in the suite can see, because the measured path is
 * the one every browser takes.
 *
 * ⚠️ The old value's justification is kept because the trap it records is real
 * and outlived its own arithmetic: the line was documented for months as
 * "1.5rem (24px)" from `py-0.5` + `text-sm`, then corrected on 2026-08-07 to a
 * measured **25.33px** on the grounds that the spans carried no text-size class
 * and rendered at the 16px default — with 26 chosen as 25.33 rounded up so a
 * fallback notch clears a whole item. The premise was right and **the figure
 * was wrong**: 16px against Tailwind preflight's `line-height: 1.5` is a 24px
 * line box plus 4px of padding = **28px**, so 26 was under by two the whole
 * time, in the direction that leaves a sliver of the next item. It is 24 now
 * because the size changed, not because the old number was corrected — both
 * are true and only the first is the reason.
 */
const FALLBACK_ITEM_HEIGHT = 24;

/**
 * The item preview. One grid row per item, tracks sized from
 * ORDER_TABLE_COLUMNS so they line up with the headers above.
 *
 * ── One item never scrolls; two or more always do ────────────────────
 * Product owner, 2026-08-07: *"update the table view so the item list is
 * always scrollable when collapsed and when number of items > 1"*. So the
 * scroll container is CONDITIONAL: an order with a single item gets no
 * overflow, no scrollbar chrome and no tab stop — there is nothing to reach,
 * and offering a scroll affordance for content that is entirely visible is
 * the "looks complete but is not" failure in reverse.
 *
 * The predicate is the ORDER's item count, not the preview length, because
 * that is what the reader means by "number of items": a degraded
 * `first_item_description` row is ONE preview line for an order of nine, and
 * that order must still scroll (it renders the line plus "+8 more").
 *
 * The bottom fade (PREVIEW_SCROLL_FADE) rides on `faded` (`total > 2`, owner
 * 2026-10-28), which implies `scrollable` — see PREVIEW_SCROLL_FADE.
 *
 * ── Clicking the preview expands the row; dragging its scrollbar does not ──
 * Product owner, 2026-08-07: *"check that clicking anywhere on the collapsed
 * row expands it please including the item list. Scrolling still needs to work
 * as well when hovering over the item list if number of items > 1."* This box
 * used to carry a blanket `onClick={e => e.stopPropagation()}`, so the item
 * columns — the widest part of the row — were the one dead zone in it. The
 * stop is gone: an ordinary click bubbles to the `<tr>` and toggles the row
 * like any other cell, and the wheel is unaffected either way because a wheel
 * event is not a click.
 *
 * What replaces it is narrower and only swallows a click that was really the
 * END OF A DRAG — grab the scrollbar, drag, release, and the row must still be
 * collapsed. Two independent signals, because they catch different gestures:
 * the pointer moved more than DRAG_SLOP px between press and release (a
 * scrollbar drag, a text swipe), or the box's own `scrollTop` changed while
 * the button was down (a scrollbar drag that ends within a few pixels of where
 * it started, or a click on the scrollbar TRACK, which pages the content
 * without moving the pointer at all).
 *
 * A click with no recorded press — a keyboard-activated click, a synthetic one
 * — is deliberately let through: the fallback is "expand", which is the
 * behaviour asked for, not the guard.
 */
function ItemPreview({ order, detail, formatMoney, testIdPrefix }) {
  const items = orderPreviewItems(order, detail);
  const total = orderItemCount(order, detail);
  const hidden = Math.max(0, total - items.length);
  const scrollable = total > 1;
  // Owner 2026-10-28: fade only from THREE items — two fit the box.
  const faded = total > 2;

  /**
   * 🟢 **THE RECEIPT IS A CLAIM ABOUT THE ORDER, NOT ONLY ABOUT THE ITEM — SO
   * IT CARRIES A STATUS TERM (2026-09-19).**
   *
   * The green item name shipped 2026-08-16 reading `Boolean(item.is_received)`
   * ALONE. That is the item half of a two-column fact, and the other column is
   * `orders.status`: `server/helpers/deliveryStatus.js#deriveDeliveryStatus`
   * turns the ticks INTO the status, and it is inert outside
   * `ordered`/`partially_delivered`/`delivered`. So a tick that survives a jump
   * back to `submitted` or `approved` — `PATCH /api/orders/:id/status` moves an
   * order backwards and only clears the ticks when the caller passes
   * `reset_side_state` — renders a row of green "received" item names on an
   * order that has not been approved, let alone placed. Reproduced through the
   * real route; the To Approve queue is where it shows, because every row there
   * is `submitted`.
   *
   * 🔑 **`RECEIVE_ENABLED_STATUSES`, NOT A NEW ARRAY.** It is the set on which a
   * line may be RECEIVED (`constants/statuses.js`), byte-for-byte the server's
   * `LINE_ORDERABLE_STATUSES`, and the pages resolve `canReceiveNow` from it —
   * so "may this row claim a receipt" and "may this row record one" cannot
   * drift onto two lists. 🔀 Since 2026-10-28 (ruling A, per-LINE) it includes
   * `approved`: a line received as soon as IT is ordered is a real receipt on
   * an approved order. A jump back BEFORE ordering clears both ticks
   * server-side, so the stale-tick case above stays closed.
   *
   * 🪤 It reads `order.status`, never `detail.status`: `ItemPreview` renders on
   * the collapsed row from the LIST payload, and `detail` is undefined until a
   * row has been expanded. A missing status is not receivable — the guard fails
   * to the plain colour, which is the safe direction.
   */
  const receiptsMayRender = RECEIVE_ENABLED_STATUSES.includes(order?.status);

  /**
   * 🚨 **THE TRACKS ARE DERIVED FROM THE LIVE WIDTHS, NOT FROM THE DECLARED
   * ONES. THIS IS THE WHOLE DIFFICULTY OF USER-RESIZABLE COLUMNS ON THIS
   * TABLE, AND IT IS INVISIBLE TO EVERY TEST IN THIS REPO.**
   * A frozen grid stops sitting under Item / Quantity / Unit Price / Total
   * Price the instant a user drags one of those four — on every row, at every
   * width. jsdom applies no stylesheet and lays nothing out, so the ALIGNMENT
   * is a browser check; what is testable here is that the arithmetic follows
   * the map, which `orderItemGridTemplate` exposes as a pure function.
   *
   * 🪤 A `null` context is not a bug and must stay supported: a row rendered
   * outside `OrderTableShell`, or inside a shell given no width props, gets the
   * measured defaults and is byte-identical to before this feature.
   *
   * The memo is keyed on the map's IDENTITY, which is stable — `useColumnWidths`
   * returns the same object unless a width actually changed — so a 100-row list
   * recomputes this once per resize rather than once per row per render.
   */
  const columnWidths = useContext(OrderColumnWidthsContext);
  const itemGrid = useMemo(() => orderItemGridTemplate(columnWidths), [columnWidths]);

  const boxRef = useRef(null);
  const pressRef = useRef(null);

  const handleMouseDown = (e) => {
    pressRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollTop: boxRef.current ? boxRef.current.scrollTop : 0,
    };
  };

  const handleClick = (e) => {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press) return;
    const moved =
      Math.abs(e.clientX - press.x) > DRAG_SLOP || Math.abs(e.clientY - press.y) > DRAG_SLOP;
    const scrolled = !!boxRef.current && boxRef.current.scrollTop !== press.scrollTop;
    if (moved || scrolled) e.stopPropagation();
  };

  /**
   * ── One wheel notch, one item ────────────────────────────────────────
   *
   * 🚨 **`e.preventDefault()` inside a React `onWheel` prop DOES NOTHING.**
   * React 17+ attaches `wheel` to the root container as a PASSIVE listener, so
   * the call is ignored and the browser logs a console warning while the
   * behaviour is unchanged. The listener has to be a native one on the box
   * itself, registered `{ passive: false }` and removed on cleanup. Anyone
   * "simplifying" this back into an `onWheel` prop reverts the fix silently.
   *
   * Attached ONLY when `scrollable` — the same `orderItemCount(...) > 1`
   * predicate that decides whether the box is a scroll container at all. A
   * single-item row is not a scroll container and must not acquire a listener.
   *
   * ── Why a CLAMP and not a fixed step ─────────────────────────────────
   * `sign(Δ) × min(|Δ|, oneItem)` caps the magnitude without replacing it, and
   * the difference matters: a trackpad emits a stream of small deltas that fall
   * UNDER the cap and pass through untouched, so trackpad scrolling stays as
   * smooth as it is today, while a mouse notch (~100px in Chrome) is cut to one
   * item. For the same reason `scrollTop` is never snapped to an item boundary
   * — snapping fights a trackpad, which is mid-item most of the time.
   *
   * ── Never trap the wheel ─────────────────────────────────────────────
   * At the top scrolling up, or at the bottom scrolling down, the event is left
   * alone — no `preventDefault` — so whatever the browser would have done with
   * it still happens. ⚠️ Note that is NOT "the page scrolls": `overscroll-
   * contain` is on this box deliberately (a flick must not jump the whole list
   * on a phone) and already blocks scroll chaining, so the page does not scroll
   * at the edges today either. This handler cooperates with that rule rather
   * than duplicating or fighting it, and adds no trapping of its own. If the
   * owner ever wants edge scrolling to reach the page, the thing to revisit is
   * `overscroll-contain`, not this.
   *
   * The drag guard above is untouched and does not interact: a wheel is not a
   * click and never writes `pressRef`. The one crossover is inherent and
   * pre-existing — wheeling with the button held changes `scrollTop`, which the
   * guard reads as "the content moved", so the release does not expand the row.
   * That is the same outcome native scrolling already produced.
   */
  useEffect(() => {
    const box = boxRef.current;
    if (!scrollable || !box) return undefined;

    const onWheel = (e) => {
      // Measured per event rather than once at mount: a late web font, a page
      // zoom or a re-render with different data all change the line box, and a
      // height captured at mount would silently desynchronise from it.
      const firstLine = box.firstElementChild;
      const measured = firstLine ? firstLine.offsetHeight : 0;
      const itemHeight = measured > 0 ? measured : FALLBACK_ITEM_HEIGHT;

      // `deltaY` is meaningless without `deltaMode`: 0 = pixels, 1 = LINES
      // (Firefox commonly sends ±3), 2 = pages. Treating a Firefox line delta
      // as pixels scrolls 3px and reads as a broken scroll area. One "line"
      // here is one item, which is what the reader means by a line in this box.
      let pixels;
      if (e.deltaMode === 1) pixels = e.deltaY * itemHeight;
      else if (e.deltaMode === 2) pixels = e.deltaY * (box.clientHeight || itemHeight);
      else pixels = e.deltaY;
      if (!pixels) return;

      // Sub-pixel tolerance on the bottom edge: `scrollHeight` is an integer
      // while `scrollTop`/`clientHeight` can be fractional under zoom, so an
      // exact comparison can decide the box is never quite at the end.
      const atTop = box.scrollTop <= 0;
      const atBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 1;
      if ((pixels < 0 && atTop) || (pixels > 0 && atBottom)) return;

      const limit = itemHeight * WHEEL_MAX_ITEMS_PER_NOTCH;
      e.preventDefault();
      box.scrollTop += Math.sign(pixels) * Math.min(Math.abs(pixels), limit);
    };

    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, [scrollable]);

  if (items.length === 0) {
    return (
      <div className={`${PREVIEW_BOX_HEIGHT} flex items-center px-2 text-sm text-gray-400 dark:text-gray-500`}>
        No items
      </div>
    );
  }

  return (
    <div
      // Two lines at rest with the third clipped, the rest reached by
      // scrolling. `overscroll-contain` stops a flick inside the preview from
      // scrolling the PAGE once it hits the end — on a phone that reads as the
      // whole list jumping, and it is why the preview may be a scroll area at
      // all. The single-item box centres its one line so the cell still looks
      // vertically aligned with its `align-middle` neighbours.
      //
      // `overflow-x-hidden` is NOT redundant beside the flexible last track in
      // ITEM_GRID, and is the product owner's literal request — *"remove
      // horizontal scroll bar"* (2026-08-07). The track fix removes the CAUSE;
      // this makes the absence STRUCTURAL, because `overflow-y-auto` alone
      // computes `overflow-x: auto` and would hand a scrollbar back to any
      // future content that overflows by a pixel. Being explicit on both axes
      // also stops the next reader rediscovering the one-axis rule the hard
      // way. It cannot hide anything the reader needs: the description is
      // ellipsed in place, the numerals are right-aligned inside definite
      // tracks, and the authoritative full list is one click away in the
      // expanded panel.
      className={
        scrollable
          ? `${PREVIEW_BOX_HEIGHT} overflow-y-auto overflow-x-hidden overscroll-contain ${faded ? `${PREVIEW_SCROLL_FADE} ` : ''}${PREVIEW_NO_NATIVE_SCROLLBAR}`
          : `${PREVIEW_BOX_HEIGHT} flex flex-col justify-center`
      }
      // A scrollable region has to be operable without a mouse (WCAG 2.1.1 —
      // axe calls this `scrollable-region-focusable`), so the box takes focus
      // and arrow keys only in the case where it can actually scroll. The
      // single-item box adds no tab stop, which matters on a 100-row page.
      tabIndex={scrollable ? 0 : undefined}
      role={scrollable ? 'group' : undefined}
      aria-label={scrollable ? `Line items (${total})` : undefined}
      data-scrollable={scrollable ? 'true' : undefined}
      data-testid={`${testIdPrefix}-item-preview-${order.id}`}
      ref={boxRef}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {items.map((item, idx) => (
        <div
          key={item.id ?? idx}
          // 🚨 `text-sm` IS LOAD-BEARING AND IS THE HALF OF THE 2026-08-12 FIX
          // THAT NOBODY WILL EXPECT TO FIND HERE. Without it these spans carry
          // no text-size class at all and inherit the 16px document default,
          // so this NESTED item block rendered one step LARGER than every
          // parent cell in its own row — which is the inversion the product
          // owner reported as "the TOTAL price is … larger than the Order
          // total". The 16px was never a decision; see `PREVIEW_BOX_HEIGHT` and
          // `FALLBACK_ITEM_HEIGHT`, both of which had already recorded it as an
          // accident that broke their arithmetic. Full reasoning, and the other
          // half of the exchange, at `TD_ORDER_TOTAL`.
          // 🔑 It goes on the LINE, not on the box: the box's other two
          // children already declare their own size (`text-sm` on "No items",
          // `text-xs` on "+N more"), so nothing is left implicit, and the line
          // box is the thing the two height constants are derived from.
          className="grid items-baseline text-sm"
          style={{ gridTemplateColumns: itemGrid }}
        >
          {/* 🟢 RECEIVED-TICK GREEN NAME (2026-08-16) — owner: "change the text
              colour of the collapsed item NAME to the same green as the
              chevron when the item has been ticked as received... Should
              share the same code." `Boolean(item.is_received)`, NEVER
              `=== true` — SQLite/pg both return the stored INTEGER 0/1 for
              this column, and `1 === true` is false, which would leave every
              received line unstyled. Fed by `preview_items`, whose allowlist
              (`server/helpers/orderPreview.js`) now carries `is_received`
              from the same batched item query every list endpoint already
              runs — no new query, one more column.
              🔻 **AND SINCE 2026-09-19 IT IS ANDed WITH `receiptsMayRender`**,
              the order-status half of the same fact — see the docblock at that
              constant. `preview_items` is NOT status-gated on the server, so
              the tick reaches this render on every status; the term belongs
              here. The `Boolean(...)` rule above is unchanged and still
              governs the item half. */}
          <span
            className={`px-2 py-0.5 truncate ${receiptsMayRender && Boolean(item.is_received) ? RECEIVED_ITEM_NAME_CLASS : 'text-gray-700 dark:text-gray-300'}`}
            title={item.description || ''}
          >
            {item.description || '-'}
            {item.is_hire ? (
              <span className="ml-1 px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                Hire
              </span>
            ) : null}
          </span>
          {/* `partial` rows come from `first_item_description`, which carries
              no numbers — show an em dash rather than a confident 0. */}
          <span className="px-2 py-0.5 text-right text-gray-700 dark:text-gray-300">
            {item.partial ? '—' : item.quantity}
          </span>
          <span className="px-2 py-0.5 text-right text-gray-600 dark:text-gray-400">
            {item.partial || item.is_hire ? '—' : formatMoney(item.unit_price)}
          </span>
          <span className="px-2 py-0.5 text-right font-medium text-gray-800 dark:text-gray-200">
            {item.partial ? '—' : formatMoney(item.line_total)}
          </span>
        </div>
      ))}
      {hidden > 0 && (
        <div className="px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400">
          +{hidden} more &mdash; expand to see all
        </div>
      )}
    </div>
  );
}

/**
 * One order: a summary `<tr>` plus, when open, an expanded `<tr>`.
 *
 * Returns a Fragment of `<tr>`s so it can sit directly inside `<tbody>`.
 */
export function OrderTableRow({
  order,
  isExpanded,
  isLoadingDetail,
  onToggleExpand,
  detail,
  expandedContent = null,
  onTitleClick = null,
  // Is this row the viewer's to act on? Decided by the PAGE
  // (`canActionOrder(order)`) and passed in — never derived here. Drives the
  // green chevron plus its tooltip and screen-reader copy; see ExpandChevron.
  actionable = false,
  // Generic per-row class hook (the retired card row had a twin, `cardClassName`).
  // It predates the actionable highlight (it landed with this component in
  // `dff5979`) and outlives it. ⚠️ The chevron branch's note here said "no
  // caller passes it today"; that premise expired the moment it met
  // expand-to-edit in this merge. 🔀 Since 2026-09-28 the dirty-edit DIMMING no
  // longer arrives here: it is `ui/PageScrollTable`'s `editingRowId`, read below
  // through `useEditingRowDim(order.id)` — one predicate shared with the hires
  // Table. This prop stays the generic per-row hook; no page passes it today.
  // Keep `actionable` ("yours to action") and dimming ("another row is
  // mid-edit") apart — never fold either into a class string to be parsed.
  rowClassName = '',
  rowTestId,
  toggleTestId,
  detailsTestId,
  chevronTestId,
  testIdPrefix,
}) {
  const { formatMoney } = useVatMode();
  // `opacity-40` while ANOTHER row holds unsaved edits (the shell's `editingRowId`).
  const editingDim = useEditingRowDim(order.id);
  const rowExtraClass = [rowClassName, editingDim].filter(Boolean).join(' ');
  // The scrollport width, for the expanded panel only — `null` outside a shell
  // and on the first paint, where the CSS fallback below takes over.
  const panelWidth = useContext(OrderTableWidthContext);
  /**
   * Which columns this row must render, from the shell (or the twelve, for a
   * row rendered outside one). See `OrderColumnsContext`.
   *
   * 🚨 **`hasCol` IS THE WHOLE SAFETY MECHANISM OF THE OPTIONAL-COLUMN FEATURE.
   * The body cells below are HAND-ORDERED JSX while the `<colgroup>` and the
   * headings are generated from this array — so a cell rendered without its
   * column, or a column with no cell, shifts every column to its right under
   * the WRONG HEADING. Silently, because a `<td>` is a `<td>`, and invisibly to
   * jsdom, which lays nothing out.**
   *
   * 🔑 Two things make that unshippable rather than merely discouraged:
   * every body cell carries **`data-col={<its key>}`**, so the cell↔column
   * association is by KEY and not by position; and
   * `OrderTableView.columns.test.js` §2 asserts, for BOTH column sets, that the
   * sequence of `data-col` on a rendered row equals the column array's own key
   * sequence (with the four item columns collapsing into their one spanning
   * cell). Both sides of that assertion derive from the array, so it cannot be
   * satisfied by editing it.
   *
   * ⚠️ **Add a `<td>` and you MUST add its `data-col` and its `hasCol` gate**;
   * the test names the offending key when you do not.
   */
  const columns = useContext(OrderColumnsContext);
  const hasCol = useCallback(
    (key) => columns.some((c) => c.key === key),
    [columns]
  );
  /**
   * DELIVERY PROGRESS (2026-08-14) — a compact received/total bar rendered
   * UNDER the Supplier value, on BOTH layouts.
   *
   * 🔀 **MOVED HERE 2026-08-16, FROM THE ORDERED BY CELL, UNCONDITIONALLY —
   * owner: consistency between Dept Orders and My Orders.** It used to render
   * under the requester name in the `ordered_by` cell, which My Orders drops
   * entirely (`MY_ORDERS_TABLE_COLUMNS`), so the bar had no home there and
   * never rendered on that page. Rather than give it a SECOND, `hasCol`-gated
   * home the two pages would show it in different cells, the owner asked for
   * ONE home on both: under Supplier, always — never branched on `hasCol`.
   * See the Supplier `<td>` below for the render site.
   *
   * 🔑 **Gated on the DATA, never on a status list.** The list endpoints attach
   * `order.delivery_info` only for the delivery-stage statuses (`ordered` /
   * `partially_delivered` / `delivered` — the last one added server-side by a
   * parallel branch), so its PRESENCE is the signal and this row never learns
   * which statuses carry it. Same pattern as the lead-time dot gating on
   * `min_slack_days` rather than re-deriving the status set. `total_items > 0`
   * guards both the divide-by-zero and an order with nothing on it.
   *
   * 🔀 **2026-10-28: TWO-TONE, from `line_progress`** (approved and later;
   * `delivery_info` as the fallback) via `orderLineProgress` + `LineProgressBar`
   * below — the SAME spot, on the phone table too. See those docblocks.
   */
  const lineProgress = orderLineProgress(order);
  const showDeliveryProgress = !!lineProgress;
  const deliveryTotal = lineProgress ? lineProgress.total : 0;
  // 🔻 THE STATUS-BASED FILL IS DELETED (2026-09-12). It read *"a `delivered`
  // order reads as fully received whatever the stored count says — the bar is a
  // completion signal, so delivered is full-and-green by definition"*, and that
  // is a claim about receipts NOBODY RECORDED. Measured on production the same
  // day: ORD-025 was overridden straight to `delivered` with nothing ever
  // ticked — the database holds 0 of 29 received — and this row drew 29/29.
  // Owner: *"we need to be careful not to say they have or have not been
  // delivered and get it wrong"*.
  // 🔑 THE BAR NOW SHOWS THE STORED COUNT, ALWAYS. From now on a delivered order
  // is genuinely fully ticked, because `PATCH /api/orders/:id/status` marks
  // every item received on the way in; a LEGACY overridden order shows its
  // honest `0/29` under a Delivered pill, which is the true state and the point.
  const deliveryReceived = lineProgress ? lineProgress.received : 0;
  const deliveryOrdered = lineProgress ? lineProgress.ordered : 0;
  /**
   * RECEIVED-BY TOOLTIP (2026-08-16) — the bar's native `title`: one line per
   * item naming who ticked it received and when. Owner: "hovering over the
   * delivery progress bar should show a tooltip with the items and who
   * received them."
   *
   * Built from the SAME `orderPreviewItems` adapter `ItemPreview` below uses
   * — `order.preview_items` first, `detail.items` once a row has been
   * expanded, then nothing — so this costs no fetch beyond what the row
   * already has. Only computed when the bar itself renders.
   */
  const deliveryTooltipText = showDeliveryProgress
    ? buildDeliveryProgressTooltip(orderPreviewItems(order, detail))
    : '';
  /**
   * The second line of the Job Name cell — the deep link's label.
   *
   * ⚠️ **CHANGED 2026-08-08: this was `getOrderTitle(order)` and is now
   * `getOrderRef(order)`.** Product owner, after the PO column shipped: *"we now
   * dont need PO number under Job name as PO has its own column"*.
   *
   * 🚨 **NOT a deletion, and that distinction is the whole change.**
   * `getOrderTitle` is `PO:<po_number>-<personal_ref>` (falling back to `PO:___`),
   * so the line is not the PO number alone — it also carries the only order
   * IDENTIFIER anywhere in this table, and it is the only click-through from the
   * table to `/orders/:id`. Deleting the line would have taken both with it,
   * neither of which he asked to lose. So the redundant `PO:<po>-` half goes and
   * the reference stays: the row reads `ORD-002` instead of `PO:___-ORD-002`, and
   * the PO number now appears exactly once, in its own column.
   *
   * 🔑 **`getOrderRef`, NOT `personal_ref`, and this is the decision worth
   * knowing.** Stripping the prefix from `getOrderTitle`'s output would leave
   * `personal_ref` — which is optional and blank on a large minority of orders,
   * so those rows would render an EMPTY, unlabelled link. `getOrderRef` is
   * `reference_number || ORD-<padded id>`: **total**, never empty for a real
   * order, and the canonical identifier `orderTitle.js`'s own header reserves for
   * *"notification messages, audit-log strings, and confirmation prompts … they
   * reach mixed audiences and need the stable canonical reference"*. A link label
   * is exactly that kind of string — the thing a buyer quotes in a message.
   * Nothing is lost: `personal_ref` stays editable and visible in the expanded
   * panel (`OrderForm.js`'s "Personal Ref"), on the order PDF, and — the part
   * that matters for a list — it is still SEARCHABLE, because
   * `utils/orderSearch.js` includes it, so typing a personal ref still finds the
   * row even though the cell no longer prints it.
   *
   * 🔴 **`getOrderTitle` ITSELF IS UNCHANGED, deliberately.** It is the single
   * source of truth for the order title and is also read by `OrderPage.js`.
   * Editing it would have changed that page too. **This is a change to what the
   * TABLE cell renders, not to the helper.** (The retired card row kept
   * `PO:<po>-<ref>` because a card had no PO column for it to be redundant with.)
   */
  const title = getOrderRef(order);
  // 🔀 2026-08-08: the string the ORDER column's second line shows and links.
  // Deliberately a SECOND variable rather than a redefinition of `title` —
  // `title` is still `getOrderRef` and is still the honest answer to "what is
  // this order called", which is what a tooltip and any future mixed-audience
  // string want. See `utils/orderTitle.js` for why the two coexist.
  const orderNumber = getOrderNumber(order);
  // 🟢 Order Completion's green PO/order-number text (2026-08-16). ONE
  // read of the shared predicate, reused below for both the PO cell's text
  // colour and the status cell's ✓ Complete badge, rather than calling
  // `isOrderComplete(order)` twice with the risk of the two silently
  // disagreeing after some future edit.
  const isComplete = isOrderComplete(order);
  // 🔑 THE CHEVRON'S TOOLTIP AND ITS `sr-only` COPY, from ONE call to the
  // shared `chevronTitle` (`constants/rowStyles.js`) — read once here so the
  // two can never disagree, exactly as `isComplete` above is read once for its
  // two consumers. `null` for a row with nothing to say, which the call site
  // spreads as NO `title` attribute (never `title=""`, an empty popup box).
  // 🪤 It is NOT derived from `actionable`: a NON-green `submitted` row now
  // names the approver it is waiting on, which is a state the old
  // `actionable ? … : undefined` shape could not express.
  const chevronState = chevronTitle(order);
  // 🔀 Was a local `showUrgencyChrome` with this expression inlined, byte-identical
  // to the card row's own copy (the card row was retired 2026-10-18). Extracted to `constants/statuses.js` on
  // 2026-08-08 when the lead-time dot would have made it a THIRD copy — and
  // renamed, because it never only gated urgency. See that file for the reasoning.
  // ⚠️ Merge note 2026-08-08: a parallel branch extracted the SAME predicate to
  // `constants/rowStyles.js` under the old name. Both landed, so for a moment
  // this file imported the name twice — a duplicate binding, which is a
  // SyntaxError and not something a careful reading of either diff would show.
  // `constants/statuses.js` is the surviving definition; the rowStyles copy is
  // deleted. Pinned by `components/orderRowTimeChrome.test.js`.
  const showTimeCriticalChrome = showsTimeCriticalChrome(order);
  // 🟢 A PLACED order (owner ruling 2026-08-14): the lead-time dot goes green
  // and the Date-cell tooltip switches to an ordered/delivery reading.
  // `showTimeCriticalChrome` still gates delivered/cancelled out entirely.
  // 🔻 Used to also silence a countdown `UrgencyBadge` here — that component
  // was RETIRED 2026-08-18 (owner ruling; see CLAUDE.md's `LeadTimeRisk.js`
  // bullet). `isPlaced` stays: `buildOrderDateTooltip` below still reads it.
  const isPlaced = isPlacedStatus(order.status);

  // The expanded frame now lives with the panel — `components/TableRowExpansion.js`.
  const expandedFrame = EXPANDED_ROW_FRAME;

  return (
    <>
      <tr
        onClick={(e) => onToggleExpand && onToggleExpand(order.id, e)}
        data-testid={rowTestId}
        className={`cursor-pointer select-none transition-colors ${isExpanded ? expandedFrame : ROW_HOVER} ${rowExtraClass}`.trim()}
      >
        {/* `title` sits on the CELL, not on the `<svg>`: the HTML `title`
            attribute produces no tooltip on an SVG element (SVG wants a
            `<title>` CHILD), and this 40px cell is the hover target anyway. */}
        <td
          data-col="expand"
          className={TD}
          data-testid={toggleTestId}
          title={chevronState || undefined}
        >
          <ExpandChevron expanded={isExpanded} actionable={actionable} testId={chevronTestId} />
          {chevronState && <span className="sr-only">{chevronState}</span>}
        </td>

        {/* PO — `orders.po_number`, already on both list payloads via
            `SELECT o.*` (`routes/orders.js:516` for GET /api/orders/department,
            `routes/approvals.js:1164` for GET /api/approvals/pending), so this
            column cost no endpoint change — unlike `order_total` and
            `can_edit`, which are computed per caller.

            ⚠️ **EMPTY IS THE COMMON CASE and the placeholder is doing real
            work.** Only 2 of the 120 orders in the test department carry a PO,
            so this column is mostly blank by nature, not by fault. It renders
            the SAME muted `-` that Job Name and Supplier use for their own
            absent values — a column of dashes reads as "nothing recorded yet",
            where a column of genuinely empty cells reads as a broken fetch.
            That confusion cost real time on 2026-08-08 over an empty supplier
            list; do not "clean up" the dash.

            **DISPLAY ONLY, deliberately.** PO already has an inline editor in
            the expanded panel (`InlinePoNumberEditor` on `DepartmentOrders.js`,
            gated on `useOrderAuthority().canManageOrder`). The owner asked for a
            COLUMN, not for editing from the collapsed row, and adding one here
            would put a permission decision inside this component — which the
            sanctioned-exception box forbids outright ("Never re-derive a
            permission inside `OrderTableView.js`"). Editing from the collapsed
            row is an obvious follow-up and would have to arrive as a node the
            PAGE builds and passes in, the way `expandedContent` does. */}
        {/* testid is `<prefix>-po-<id>`, following the `<prefix>-total-<id>`
            precedent below: NOT `<prefix>-order-po-<id>`, because Approvals
            names its ROWS `approval-order-<id>` and anything starting
            `approval-order-` is picked up by a row-counting selector. */}
        {/* 🔀 TWO LINES SINCE 2026-08-08: PO on top, order number beneath — and
            the ORDER NUMBER LINE IS THE CLICK-THROUGH, moved here from Job Name.

            🔑 **THE CONDITIONAL IS THE DESIGN, not a tidy-up.** With PO on top
            and 99.5% of orders carrying no PO, an unconditional two-line cell
            would render a blank line above the order number on 1,728 of 1,736
            rows — which reads as a rendering fault, not as "no PO recorded".
            So a PO-less order renders the order number ALONE. That is what
            makes "PO on top" (the owner's explicit instruction, given against
            my recommendation) look deliberate rather than broken. Do not
            "simplify" this into always rendering both lines.

            🔴 **NO MUTED `-` PLACEHOLDER FOR AN ABSENT PO, and this REVERSES
            the rule the previous version of this cell argued for.** That rule
            was right for a single-line cell: a column of dashes reads as
            "nothing recorded yet" where a column of empty cells reads as a
            broken fetch. It is wrong here, because the cell is no longer empty
            — the order number is always there, so there is no empty-column
            ambiguity left to resolve, and a dash above every order number would
            be 1,728 rows of visual noise saying nothing. The dash survives
            unchanged in Job Name and Supplier, where the ambiguity is real.

            **Row height is unchanged and that was a requirement** (owner: *"try
            to not increase the collapsed row height"*). Job Name gave up its
            second line in the same change, so the tallest cell in the row is
            the same two lines it always was — `leading-tight` on both lines and
            `text-xs` on the second match what Job Name's stacked pair used. */}
        <td
          data-col="po_number"
          className={TD}
          data-testid={`${testIdPrefix}-po-${order.id}`}
        >
          {/* The top line ALONE is `components/PoOrderTopLine.js` — extracted
              2026-09-28 when the hires Table needed the identical stack
              (`components/hires/HireTableView.js`). Everything below this
              line (the click-through, the markers, completed-order styling)
              stays here: it is order-only and the hires row has no use for
              it. */}
          <PoOrderTopLine
            poNumber={order.po_number}
            testId={`${testIdPrefix}-po-value-${order.id}`}
            className={isComplete ? COMPLETED_ORDER_NUMBER_CLASS : ''}
          />
          {/* ⚠️ `orderNumber`, NOT `title`. `title` is `getOrderRef`, which
              prefers `reference_number` (`AURORA-ORD-002`); this cell shows
              `ORD-002`. Both helpers live in `utils/orderTitle.js` with the
              reason for the split written out — the short form is what makes a
              90px two-line cell affordable at all. */}
          {/* Order Completion's delivery-paperwork marker (2026-08-16) sits
              beside the order number. Nested INSIDE the order-number element
              (never as an extra sibling) so `poCell().children` and
              `poCell().lastElementChild` — pinned by
              `OrderTableView.itemPreview.test.js` §9 — are unchanged; the
              marker renders no text, so `.textContent` and `title` on that
              element are unaffected too. */}
          {/* 🔀 THE ATTACHMENT (PAPERCLIP) MARKER JOINED IT HERE 2026-08-16,
              UNCONDITIONALLY ON BOTH LAYOUTS — owner: consistency between
              Dept Orders and My Orders. It used to live in the `ordered_by`
              cell (Dept Orders only; My Orders drops that column and never
              rendered it at all). Rather than give it a SECOND, `hasCol`-gated
              home the two pages would show it in different cells, it now has
              ONE home, next to the newspaper, on every layout — never branched
              on `hasCol`. Same nesting rule as the newspaper: INSIDE the
              order-number element, never a sibling `<td>` child, so the same
              `poCell().children` / `lastElementChild` pins hold. `file_count`
              now comes from `GET /api/orders` too (`helpers/orderFileTooltips.js`),
              so it renders on My Orders for the first time.
              🚨 CHILD-`title`-SHADOW RULE: both markers carry their OWN native
              `title` (built from their filename lists) on their own wrapper
              `<span>`, never on this element — a `title` on a CHILD wins over
              its parent's, which is exactly the two-zone behaviour wanted:
              hovering a clip shows ITS files, hovering the order-number text
              elsewhere still shows `title={orderNumber}`. Neither marker's
              `title` shadows the other's — each is its own sibling span,
              never nested inside the other. */}
          {/* 🟢 Completed-order green text (2026-08-16) — owner: "lets change
              the colour of the PO and order numbers to the same chevron
              green on completed orders please." TEXT ONLY: the markers above
              and their tooltips are untouched, and the link's `hover:underline`
              affordance survives on the clickable branch. */}
          {onTitleClick ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onTitleClick(order); }}
              title={orderNumber}
              className={`text-xs font-semibold hover:underline truncate max-w-full text-left leading-tight ${isComplete ? COMPLETED_ORDER_NUMBER_CLASS : 'text-blue-600 dark:text-blue-400'}`}
            >
              {orderNumber}
              <OrderDeliveryPaperworkMarker present={order.has_delivery_paperwork} files={order.delivery_files} className="ml-1" />
              <OrderAttachmentMarker count={order.file_count} files={order.attachment_files} className="ml-1" />
            </button>
          ) : (
            <div className={`text-xs font-semibold truncate leading-tight ${isComplete ? COMPLETED_ORDER_NUMBER_CLASS : 'text-gray-500 dark:text-gray-400'}`} title={orderNumber}>
              {orderNumber}
              <OrderDeliveryPaperworkMarker present={order.has_delivery_paperwork} files={order.delivery_files} className="ml-1" />
              <OrderAttachmentMarker count={order.file_count} files={order.attachment_files} className="ml-1" />
            </div>
          )}
        </td>

        {/* Date — `order_date`, the date the order was RAISED, which is NOT
            `created_at`: they differ on 120 of the 1,736 orders in the dev
            database (checked read-only in `server/data/ordering.db`) — imported
            rows (restore, migration, seeds) keep their own dates; nothing in the
            app edits `order_date` (corrected 2026-11-02, this said "user-settable").
            Rendering `created_at` here would quietly disagree with the value in
            the expanded panel for those rows.

            ⚠️ **This cell must stay in step with the column's position in
            `ORDER_TABLE_COLUMNS`** — the `<colgroup>` and the header are driven
            by that array, but the body cells are hand-ordered JSX, so the two can
            silently disagree and every column right of the mistake shifts under
            the wrong heading. Date sits SECOND, between PO and Ordered By, by the
            owner's explicit choice.

            `formatDateCompact` — the shared util, never an inline
            `toLocaleDateString`. It returns '-' for a null/invalid date, so the
            muted-placeholder span is only for a MISSING value; wrapping the
            util's own '-' would double it up. `whitespace-nowrap` because
            "25 Apr 26" must not wrap to two lines in a 74px content box and push
            the row taller than its neighbours.

            🔀 **TWO LINES SINCE 2026-08-08 — order date on top, REQUIRED-BY
            beneath with the lead-time dot beside it.** Product owner: *"how about
            the date column for orders is a two liner? top line is ordered date
            and bottom line is required by date? then we can add the slack dot
            next to the bottom line date?"*

            🔑 **THIS ANSWERS THE OBJECTION THAT PUT THE DOT IN THE STATUS CELL
            HOURS AGO.** The dot was moved OUT of this column then because the
            column showed only `order_date` — the date the order was RAISED — while
            slack is measured against the NEEDED-BY date, so a dot here would have
            been tied to the wrong date. That reasoning was right; this fixes it
            the other way round, by putting the deadline IN the column rather than
            the dot somewhere else. The dot is now beside the date it is about.

            🚨 **`required_by_date` IS A SERVER FIELD AND MUST NOT BE RE-DERIVED
            HERE.** It comes from `computeSlackDetail`, which captures the deadline
            in the SAME branch that lowers the minimum slack — so it is, by
            construction, the deadline the DOT is about. The obvious client-side
            shortcuts are both wrong: `custom_needed_by_date` is populated on ONE
            real order and is ignored by the slack maths unless `urgency` is
            `custom`, and "the earliest item date" is wrong because a long lead
            time can make a later-dated item the binding constraint. See
            `server/helpers/leadTimeHelpers.js`.

            🔴 **NO DEADLINE → NO SECOND LINE AND NO DOT.** They are absent
            together for free: `computeSlackDetail` returns both as null when no
            item has a resolvable deadline, so there is no second condition to
            keep in step. Never render a dot without its date.

            ⚠️ **THE DATE SHOWS ON A DELIVERED/CANCELLED ORDER; THE DOT DOES NOT.**
            `showsTimeCriticalChrome` gates ONLY the dot here — which is not the
            natural assumption, hence this note. A deadline is factual history and
            worth reading on a delivered order, and keeping the line stops the
            column jumping between one and two lines as orders complete. The dot
            is a live warning and is meaningless once the thing has arrived.

            🔀 **ONE TOOLTIP FOR THE WHOLE CELL SINCE 2026-08-08** — owner: *"No
            need for two tool tips for each row just one tool tip give us all the
            info we need"*. It was actually THREE nested `title` attributes (this
            `<td>`, which showed the **raw ISO string**; the required-by line; and
            `LeadTimeDot`'s own), and native `title` resolves innermost-wins, so
            what you saw depended on which pixel you were over. The string is
            built by `buildOrderDateTooltip` in `components/LeadTimeRisk.js` —
            **the wording lives there, beside `getLeadTimeBand` and the pill's own
            sentences, so the surfaces cannot drift**; this file passes fields and
            renders the result. `LeadTimeDot` lost its `title` in the same change
            (it kept `role="img"` + `aria-label`) because a child's title would
            shadow this one. ⚠️ **`title={undefined}` renders NO attribute** — the
            builder returns undefined rather than `''` when it has nothing to say,
            so hovering gives no empty tooltip box.

            🔀 **AND IT IS NOW TWO LINES CARRYING ONLY WHAT THIS CELL DOES NOT
            SAY (2026-08-08)** — owner, live in the browser: *"remove the date it
            was ordered as thats already in the cell. show the order now/ orderin
            3 days then put leadtime on the next line. no need for needed by date
            as thats also in the main cell"*. Both dates were being printed twice
            — once by the two `<div>`s below, once inside the tooltip — so the
            tooltip now reads `Order within 3 days\n5 day lead time` and NEITHER
            date is passed to the builder. 🔑 **`order.order_date` is no longer
            an argument at all**, and `required_by_date` is passed as a PRESENCE
            test only. ⚠️ **A delivered/cancelled order now gets NO tooltip**, not
            the old facts-only form: with both dates removed that branch had
            nothing left to say. The property that matters is unchanged — a
            finished order never gets an instruction.

            🟢 **`orderLeadTimeDays: order.lead_time_days` ADDED 2026-08-16 —
            NOT the same field as `leadDays` above.** It feeds the builder's
            no-required-by branch ONLY, which cannot use `min_slack_lead_days`
            because `computeSlackDetail` returns that null together with
            `required_by_date` whenever there is no deadline. `order.lead_time_days`
            is the RAW `orders.lead_time_days` column, already on every order row
            from this endpoint's `SELECT o.*` — no new server field. See
            `buildOrderDateTooltip`'s own docblock for why the two must never be
            confused. */}
        <td
          data-col="order_date"
          className={`${TD} whitespace-nowrap`}
          title={buildOrderDateTooltip({
            requiredByDate: order.required_by_date,
            orderDate: order.order_date,
            slackDays: order.min_slack_days,
            leadDays: order.min_slack_lead_days,
            showTimeCritical: showTimeCriticalChrome,
            placed: isPlaced,
            orderLeadTimeDays: order.lead_time_days,
            // 🟢 2026-08-18 owner ruling — a FINISHED order can now get a
            // fact-only tooltip (delivered/partially_delivered/cancelled/
            // rejected). `status` picks the terminal branch; the other three
            // are server-computed fields the list endpoints attach — absent
            // (`undefined`) on any order/endpoint that doesn't carry them, in
            // which case the builder renders nothing for those branches, same
            // as before this change. See `buildOrderDateTooltip`'s docblock.
            status: order.status,
            mostRecentReceivedDate: order.most_recent_received_date,
            // 🟢 2026-08-19 owner ruling — the delivered tooltip's WHO.
            // Already on every row from this endpoint's `SELECT o.*` in the
            // sense that the SERVER attaches it beside `most_recent_received_date`
            // (`server/routes/orders.js`) — no new client-side computation.
            receivedByNames: order.received_by_names,
            cancellation: order.cancellation_info,
            rejection: order.rejection_info,
            // 🔀 2026-08-19 — a buyer-set real ETA (`PATCH .../expected-delivery`)
            // overrides the computed "Expected in Y days" guess in both placed
            // branches. Already on every row from this endpoint's `SELECT o.*`
            // — no new server field, same as `orderLeadTimeDays` above.
            expectedDeliveryDate: order.expected_delivery_date,
          })}
        >
          <div className="truncate leading-tight">
            {order.order_date
              ? formatDateCompact(order.order_date)
              : <span className="text-gray-400 dark:text-gray-500">-</span>}
          </div>
          {order.required_by_date && (
            <div
              className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 leading-tight"
              data-testid={`${testIdPrefix}-required-by-${order.id}`}
            >
              {/* 🔴🟠 2026-09-13 owner ruling — a PLACED order's dot is about the
                  DELIVERY, not the placement slack. These two props are the SAME
                  two values handed to `buildOrderDateTooltip` above (`leadDays`
                  and `expectedDeliveryDate`), passed through the shared
                  `deliveryOffsetDays` helper by both, so the dot and the cell's
                  tooltip cannot disagree — which is the defect he reported.
                  🚨 Do NOT substitute `order.lead_time_days` for
                  `min_slack_lead_days` here: see the tooltip's `orderLeadTimeDays`
                  note above for why the two are not interchangeable. */}
              {showTimeCriticalChrome && (
                <LeadTimeDot
                  slackDays={order.min_slack_days}
                  status={order.status}
                  leadDays={order.min_slack_lead_days}
                  expectedDeliveryDate={order.expected_delivery_date}
                />
              )}
              <span className="truncate">{formatDateCompact(order.required_by_date)}</span>
            </div>
          )}
        </td>

        {/* Ordered By — the requester. `UserName` carries the deactivated
            treatment (amber name + tooltip) and is the SAME component every
            other surface uses. Never inline the colour here: that is how
            surfaces drift. */}
        {/* 🔻 OPTIONAL SINCE 2026-08-12 — My Orders omits this column entirely
            (`MY_ORDERS_TABLE_COLUMNS`), because every row there is the viewer's
            own order and the cell rendered one constant string. Owner: *"on my
            orders we can drop the ordered by column as you said."*
            🚨 THE GATE AND THE COLUMN ARRAY MUST AGREE, AND `hasCol` IS WHAT
            MAKES THEM — never test a page name, a prop or a testid prefix here;
            this component must not learn which surface it is on, and a gate that
            disagreed with the `<colgroup>` would shift Job Name, Supplier, the
            item block, Order Total and Status one column left of their headings,
            silently. */}
        {/* 🔀 NAME-ONLY SINCE 2026-08-16 — both the attachment marker and the
            delivery progress bar MOVED OUT of this cell, to the PO/order-number
            cell and the Supplier cell respectively, on BOTH layouts (owner:
            consistency between Dept Orders and My Orders, since My Orders has
            no Ordered By cell for either to live in). See those two cells'
            own docblocks for the reasoning. This cell went back to a plain
            `truncate`d `<td>` — no flex row, no `min-w-0`, no marker. */}
        {hasCol('ordered_by') && (
          <td data-col="ordered_by" className={`${TD} truncate`} title={order.requested_by || ''}>
            <UserName name={order.requested_by} isActive={order.is_active} />
          </td>
        )}

        {/* Job Name — `orders.job_rig`, the "Job / Rig / Gag" field. On the
            list payload already; the ORDER REFERENCE goes under it so the row is
            still identifiable and clickable.

            🪞 That sentence was ASPIRATIONAL until 2026-08-08 and now describes
            reality: this line rendered `getOrderTitle` — `PO:<po>-<personal_ref>`
            — not the reference, and the comment (and the test that called it "the
            reference-number link") had said otherwise for as long as it existed.
            It is `getOrderRef` now; see the `title` docblock above for why the
            shared helper was NOT the thing changed.

            🔑 **ONE `title` variable feeds BOTH branches, which is what keeps the
            clickable and non-clickable variants consistent by construction.** The
            only difference between them is the affordance — a blue link when the
            page supplies `onTitleClick`, muted static text when it does not
            (`Approvals.js` passes no handler). Never compute the string twice
            here: two expressions is how the two variants drift, and the
            non-clickable one is the easier to forget because no test clicks it.

            `title={title}` so a long reference stays readable when `truncate`
            clips it — the same treatment every other text cell in this table
            uses, and newly worth having because the string being shown changed. */}
        {/* 🔀 SINGLE LINE AGAIN SINCE 2026-08-08 — the row's identity line
            moved to the Order column, where it sits under the PO number. It is
            the ORDER NUMBER (`getOrderNumber`, `ORD-<id>`), NOT
            `reference_number`: this comment said "the reference line" until
            2026-08-12 and that was never what the cell rendered. The docblock
            above describes the two-line version and is kept only for the
            history of what the second line held and why it was never
            `personal_ref`; the live rule is:

            🔑 **the whole cell is now just `job_rig`, and this column NO LONGER
            CARRIES THE ROW'S IDENTITY OR ITS CLICK-THROUGH.** Both moved with
            the reference. That is what pays for the Order column's second line
            without the row getting taller — the height was the owner's stated
            constraint, and a column cannot gain a line unless another gives one
            up. If you ever restore a second line here, take one off the Order
            column in the same change or the collapsed row grows on every page.

            `title` (i.e. `getOrderRef`) is still computed above and is NOT
            rendered here. 🔴 **It is dead, and under the 2026-08-12 ruling it
            stays dead.** `ORD-<id>` is now the canonical identifier everywhere
            — screen, supplier PDF, email, export — and `reference_number` is
            displayed nowhere. Owner: *"so all we need to show and use for
            searches is the database ord id and PO if one exists"*, the database
            already knowing each order's department and production, so the
            `AURORA-` prefix carries nothing the system needs. So the Order
            column is deliberately NOT the reference and never will be, and
            `getOrderNumber` is no longer a "table-only short form": it is THE
            order number. `reference_number` remains SEARCHABLE
            (`utils/orderSearch.js`) so an old PDF quoting `AURORA-ORD-120`
            still resolves — it stopped being shown, it did not stop being
            findable. */}
        <td data-col="job_name" className={`${TD} truncate`} title={order.job_rig || ''}>
          {order.job_rig || <span className="text-gray-400 dark:text-gray-500">-</span>}
        </td>

        {/* 🔀 TWO-LINE CELL SINCE 2026-08-16 — supplier name on top, the
            delivery progress bar beneath, on BOTH layouts. Owner: consistency
            between Dept Orders and My Orders (the bar's old home, the
            `ordered_by` cell, does not exist on My Orders). `truncate` moved
            OFF the `<td>` and onto the name's own `<div>` — same shape as the
            Date cell's two-line pattern below, and for the same reason: the
            `<td>` now holds two stacked children and `truncate` on the cell
            itself would only ever clip whichever line happens to be first in
            the DOM. */}
        <td data-col="supplier" className={TD} title={order.supplier_name || ''}>
          <div className="truncate leading-tight">
            {order.supplier_name || <span className="text-gray-400 dark:text-gray-500">-</span>}
          </div>
          {/* Delivery progress — bar + count on ONE flex row so it adds a
              single line under the supplier name, not two. Reuses the shared
              `ProgressBar` (ghost track + green fill + internal pct/guard) per
              the Code Unification Principle; the "N/M" sits beside it
              (`ProgressBar`'s own `showLabel` puts the count first, with a
              suffix — not this shape).

              🔀 CARRIES A NATIVE `title` SINCE 2026-08-16 (RECEIVED-BY
              TOOLTIP) — a deliberate CHILD `title` on this wrapper, not the
              `<td>`. `ProgressBar` itself renders none, so there is nothing
              of ITS to shadow; a child `title` DOES win over a parent's, and
              that is the wanted behaviour here: hover the bar for the
              per-item received list, hover the name (or elsewhere in the
              cell) for the cell's own `title={supplier_name}`, unaffected.
              `title={deliveryTooltipText || undefined}` rather than `''` —
              an empty string still renders a blank tooltip box. */}
          {showDeliveryProgress && (
            <LineProgressBar
              progress={lineProgress}
              title={deliveryTooltipText || undefined}
            />
          )}
        </td>

        {/* Item / Quantity / Unit Price / Total Price — ONE cell spanning the
            four so the preview scrolls as a single block. `p-0` because the
            inner grid supplies its own per-cell padding; see ITEM_GRID.

            🚨 **COUNTS `ITEM_COLUMNS`, NOT `ORDER_TABLE_COLUMNS` — and the
            asymmetry with the expanded-panel cell below is the point, not an
            oversight.** This span must equal the width of the ITEM BLOCK (4);
            the panel below must equal the width of the WHOLE ROW (11 since the
            PO column landed). Adding a data column outside the block raises the
            second and must leave this one alone — "make them consistent" is
            exactly the change that silently misaligns the preview grid under
            its own headers, which jsdom cannot see. */}
        <td data-col="item" className="p-0 align-middle" colSpan={ITEM_COLUMNS.length}>
          <ItemPreview order={order} detail={detail} formatMoney={formatMoney} testIdPrefix={testIdPrefix} />
        </td>

        {/* testid is `<prefix>-total-<id>`, NOT `<prefix>-order-total-<id>`:
            Approvals gives its ROWS `approval-order-<id>`, so the latter would
            collide under any `^="approval-order-"` selector and a test
            counting rows would silently pick up total cells too. */}
        <td data-col="order_total" className={TD_ORDER_TOTAL} data-testid={`${testIdPrefix}-total-${order.id}`}>
          {formatMoney(order.order_total ?? order.total_amount ?? 0)}
        </td>

        {/* 🔀 **THE LEAD-TIME DOT WAS HERE FOR A FEW HOURS ON 2026-08-08 AND HAS
            MOVED TO THE DATE CELL. Do not put it back.** It sat here because the
            Date column showed only `order_date` — the date the order was RAISED —
            and a dot beside that would have been tied to the wrong date. That
            objection was correct and the owner answered it directly: *"how about
            the date column for orders is a two liner? … then we can add the slack
            dot next to the bottom line date?"* The deadline moved INTO the column,
            so the dot now sits beside the date it is actually about.
            ⚠️ **STILL NO `LeadTimeRisk` PILL HERE, and that remains an open
            PRODUCT question rather than an oversight to close on sight** — the
            dot is not the pill. The retired card row rendered a pill beside its
            urgency badge; this table never has, and adding one is a
            real decision (a fourth chip in a fixed-width cell of a table already
            at a 1480px floor), so it needs the owner.
            The predicate for time-critical chrome is `showsTimeCriticalChrome`. */}
        <td data-col="status" className={TD}>
          <div className="flex flex-wrap items-center gap-1">
            <StatusBadge status={order.status} order={order} />
            {/* Order Completion's ✓ Complete badge (2026-08-16) — ONE shared
                predicate (`isOrderComplete`, `constants/statuses.js`), never a
                re-derivation here. */}
            {isOrderComplete(order) && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                data-testid="order-complete-badge"
              >
                ✓ Complete
              </span>
            )}
            {/* 🔑 THE DELIVERED-ORDER PROMPT (2026-09-11) — ONE shared
                predicate (`deliveredOrderPrompt`, `constants/statuses.js`),
                never a re-derivation here, exactly like the badge above.
                Owner: *"the buyer doesn't know WHY a delivered order is still
                not completed"*. It renders nothing unless the order is
                `delivered` AND incomplete, so it is invisible on the great
                majority of rows. 🔴 A PROMPT, NEVER A GATE — paperwork is
                optional by ruling; see the component's docblock. */}
            <DeliveredOrderPill order={order} />
          </div>
        </td>

      </tr>

      {/* Always rendered: `open` lets the panel ease CLOSED as well as open
          (`hooks/useExpandAnimation.js`); it renders nothing once closed. */}
      <ExpandedPanelRow
          open={isExpanded}
          colSpan={columns.length}
          panelWidth={panelWidth}
          testId={detailsTestId}
          isLoading={isLoadingDetail}
          detail={detail}
        >
          {/* 📱 `data-mobile-compact`: one type step smaller below md — index.css */}
          <div data-mobile-compact="">{expandedContent}</div>
        </ExpandedPanelRow>
    </>
  );
}

/**
 * "Jane Smith" → "Jane S." — the phone table's compact Ordered by (first name
 * + surname initial; the full name rides in the cell's `title`). One word stays
 * as it is; blank stays blank. Display only, never an identity. Exported
 * (2026-10-01) for the hires phone table's Requested by — never re-rolled.
 */
export function compactPersonName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

/**
 * THE PHONE row, rendered against a phone column set —
 * `MY_ORDERS_MOBILE_TABLE_COLUMNS`, `DEPT_ORDERS_MOBILE_TABLE_COLUMNS` or
 * `APPROVALS_MOBILE_TABLE_COLUMNS` (above). Same props and testids as
 * `OrderTableRow` so a page swaps one for the other and nothing else; same
 * expanded panel, same `expandedContent` node — so it can re-derive no
 * permission (the sanctioned-exception rule).
 * 🔑 **Body cells are emitted IN THE COLUMN ARRAY'S ORDER, by key** (since
 * 2026-10-01, when a second and third column set arrived): each cell is built
 * once below and `columns.map` places it, so a column set can never put a cell
 * under the wrong heading. A key with no cell here renders nothing — and the
 * `data-col` sequence test fails.
 * 📅 **Needed-by date + lead-time dot**: in the Job cell when the Job column
 * says `jobDate: 'required_by'` (Dept Orders, To Approve); otherwise (My
 * Orders, the owner's placement) at the top of the EXPANDED row. Same fields
 * and the same `showsTimeCriticalChrome` gate as the desktop Date cell either
 * way, so the layouts cannot disagree about the deadline.
 */
/**
 * THE ORDER LIST'S LINE PROGRESS (2026-10-28, per-line "ordered") — owner:
 * *"we can show a progress bar for ordred litems like we have for delive3rd
 * items? maybe a slightly lighter green so on the 'All' tab we can
 * differentiate"*. Read from the list payload's `line_progress`
 * (`{ total_items, ordered_items, received_items }`, goods lines only, on
 * approved and later — `server/helpers/deliveryInfo.js`). Falls back to
 * `delivery_info` (ordered = total when it carries no `ordered_items`: a
 * placed order's lines are ordered, the server heals them). Null → no bar.
 * Gated on the DATA's presence, never a status list — the 2026-08-14 rule.
 */
export function orderLineProgress(order) {
  const lp = order && (order.line_progress || order.delivery_info);
  if (!lp) return null;
  const total = Number(lp.total_items);
  if (!Number.isFinite(total) || total <= 0) return null;
  const received = Math.min(total, Number(lp.received_items) || 0);
  const rawOrdered = lp.ordered_items == null && !order.line_progress ? total : Number(lp.ordered_items) || 0;
  // A received line is ordered (server invariant) — never draw delivered past ordered.
  const ordered = Math.min(total, Math.max(rawOrdered, received));
  return { total, ordered, received };
}

/** The two-tone bar, full cell width. Dark green = delivered, faded light green
 *  = ordered not yet delivered, grey = to order. NO visible label (owner 2026-10-28:
 *  "the bar and the tool tip give us the info" — the label also made bars different
 *  lengths): the full sentence is the `title` and the bar's `aria-valuetext`. */
function LineProgressBar({ progress, title, compact = false, inline = false }) {
  const { total, ordered, received } = progress;
  const words = lineProgressLabel(ordered, received, total);
  // `inline`: beside the phone Job cell's date (2026-10-28), filling the rest of the line.
  const shell = inline
    ? 'flex-1 min-w-[2rem] flex items-center leading-none'
    : compact ? 'mt-0.5 flex items-center gap-1 leading-none' : 'mt-1 flex items-center gap-1.5';
  return (
    <span data-testid="order-delivery-progress" className={shell} title={title}>
      <ProgressBar
        done={received}
        pending={ordered - received}
        total={total}
        perItem
        valueText={words}
        ariaLabel="Line items ordered and delivered"
        className="flex-1 min-w-[2rem]"
      />
    </span>
  );
}

export function OrderMobileTableRow({
  order,
  isExpanded,
  isLoadingDetail,
  onToggleExpand,
  detail,
  expandedContent = null,
  // Decided by the PAGE (`canActionOrder`), never here — the green chevron.
  actionable = false,
  rowTestId,
  toggleTestId,
  detailsTestId,
  chevronTestId,
  testIdPrefix,
}) {
  const { formatMoney } = useVatMode();
  const editingDim = useEditingRowDim(order.id);
  const panelWidth = useContext(OrderTableWidthContext);
  const columns = useContext(OrderColumnsContext);
  const orderNumber = getOrderNumber(order);
  const isComplete = isOrderComplete(order);
  const mobileLineProgress = orderLineProgress(order);
  const chevronState = chevronTitle(order);
  const showTimeCriticalChrome = showsTimeCriticalChrome(order);
  const items = orderPreviewItems(order, detail);
  const firstItem = items.length > 0 ? (items[0].description || '') : '';
  const moreCount = Math.max(0, orderItemCount(order, detail) - 1);
  const colOf = (key) => columns.find((c) => c.key === key) || {};
  const neededByInJob = colOf('job_name').jobDate === 'required_by';
  // `h-11` on a cell is a MINIMUM row height of 44px — the tap-target floor.
  const TDM_TEXT = 'h-11 text-sm text-gray-700 dark:text-gray-300';
  const TDM = `${TD_LAYOUT} ${TDM_TEXT}`;
  const TDM_JOB = `pl-1 pr-2 py-2 align-middle ${TDM_TEXT}`;
  const jobLineBar = mobileLineProgress && (
    <LineProgressBar
      progress={mobileLineProgress}
      inline
      title={lineProgressLabel(mobileLineProgress.ordered, mobileLineProgress.received, mobileLineProgress.total)}
    />
  );
  const mutedDash = <span className="text-gray-400 dark:text-gray-500">-</span>;
  const neededByDot = showTimeCriticalChrome && (
    <LeadTimeDot
      slackDays={order.min_slack_days}
      status={order.status}
      leadDays={order.min_slack_lead_days}
      expectedDeliveryDate={order.expected_delivery_date}
    />
  );

  const cells = {
    /* PO / Order — NOT frozen (owner, 2026-09-30, from a real Android
       phone: *"do NOT pin/freeze the PO/Order column on mobile (it
       scrolls with the rest)"*). The chevron lives here (no expand
       column on a phone), and so does the toggle testid.
       📏 Tighter than `TD_LAYOUT` (2026-10-01, owner): `pl-1 pr-0.5`
       around the chevron + ORD id, `gap-0.5` between them, and the next
       cell starts `pl-1` — the 44px row floor (`h-11`) is untouched. */
    po_number: (
      <PhonePoOrderCell
        key="po_number"
        testId={toggleTestId}
        innerTestId={`${testIdPrefix}-po-${order.id}`}
        expanded={isExpanded}
        actionable={actionable}
        chevronTestId={chevronTestId}
        chevronState={chevronState}
        poNumber={order.po_number}
        poTestId={`${testIdPrefix}-po-value-${order.id}`}
        orderNumber={orderNumber}
        complete={isComplete}
        hasDeliveryPaperwork={order.has_delivery_paperwork}
        deliveryFiles={order.delivery_files}
        fileCount={order.file_count}
        attachmentFiles={order.attachment_files}
      />
    ),

    /* Job, with a grey second line: the ORDER date (`order_date`, "25 Apr")
       on My Orders, the NEEDED-BY date + lead-time dot where the column asks
       (`jobDate: 'required_by'`) — the desktop Date cell's second line, with
       its tooltip. 🔀 An EMPTY job renders NOTHING (2026-10-01, owner:
       *"empty Job shows nothing (not "-")"*) — the date line then sits alone. */
    job_name: (
      <td
        key="job_name"
        data-col="job_name"
        className={TDM_JOB}
        title={neededByInJob
          ? [order.job_rig, buildOrderDateTooltip({
            requiredByDate: order.required_by_date,
            orderDate: order.order_date,
            slackDays: order.min_slack_days,
            leadDays: order.min_slack_lead_days,
            showTimeCritical: showTimeCriticalChrome,
            placed: isPlacedStatus(order.status),
            orderLeadTimeDays: order.lead_time_days,
            status: order.status,
            mostRecentReceivedDate: order.most_recent_received_date,
            receivedByNames: order.received_by_names,
            cancellation: order.cancellation_info,
            rejection: order.rejection_info,
            expectedDeliveryDate: order.expected_delivery_date,
          })].filter(Boolean).join('\n')
          : (order.job_rig || '')}
      >
        {/* Owner 2026-11-01: a blank job says so, or the grey date alone made the column read as a date. */}
        {order.job_rig ? (
          <div className="truncate leading-tight" data-testid={`${testIdPrefix}-job-${order.id}`}>{order.job_rig}</div>
        ) : (
          <div className="truncate leading-tight text-gray-400 dark:text-gray-500" data-testid={`${testIdPrefix}-no-job-${order.id}`}>No job name</div>
        )}
        {/* 🔀 2026-10-28 (owner, yes to "move the bar under PO/Job on phones"): the
            line bar rides the date line, in the always-visible second column — it
            was in the Supplier cell, off-screen at 390px. BESIDE the date, not under
            it, so the row stays two lines inside its 44px floor. A row with no line
            progress renders the cell as before. */}
        {neededByInJob ? (
          (order.required_by_date || jobLineBar) && (
            <div
              className="flex items-center gap-1 min-w-0 text-xs leading-tight text-gray-500 dark:text-gray-400"
              data-testid={`${testIdPrefix}-required-by-${order.id}`}
            >
              {order.required_by_date && neededByDot}
              {order.required_by_date && <span className="truncate">{formatDateCompact(order.required_by_date)}</span>}
              {jobLineBar}
            </div>
          )
        ) : jobLineBar ? (
          <div className="flex items-center gap-1 min-w-0 text-xs leading-tight text-gray-500 dark:text-gray-400">
            <span className="truncate shrink-0" data-testid={`${testIdPrefix}-order-date-${order.id}`}>
              {formatDateDayMonth(order.order_date)}
            </span>
            {jobLineBar}
          </div>
        ) : (
          <div
            className="truncate text-xs leading-tight text-gray-500 dark:text-gray-400"
            data-testid={`${testIdPrefix}-order-date-${order.id}`}
          >
            {formatDateDayMonth(order.order_date)}
          </div>
        )}
      </td>
    ),

    /* Ordered by — compact (first name + surname initial), the full name in
       the cell's `title`; `UserName` keeps the deactivated treatment. */
    ordered_by: (
      <td key="ordered_by" data-col="ordered_by" className={`${TDM} truncate`} title={order.requested_by || ''}>
        {order.requested_by
          ? <UserName name={compactPersonName(order.requested_by)} isActive={order.is_active} />
          : mutedDash}
      </td>
    ),

    supplier: (
      <td key="supplier" data-col="supplier" className={`${TDM} truncate`} title={order.supplier_name || ''}>
        {order.supplier_name || mutedDash}
      </td>
    ),

    /* Item — the FIRST item and "+N more", ONE cell (never the item block). */
    item: (
      <td key="item" data-col="item" className={TDM} title={firstItem}>
        <div className="truncate leading-tight" data-testid={`${testIdPrefix}-first-item-${order.id}`}>
          {firstItem || mutedDash}
        </div>
        {moreCount > 0 && (
          <div
            className="truncate text-xs leading-tight text-gray-500 dark:text-gray-400"
            data-testid={`${testIdPrefix}-more-items-${order.id}`}
          >
            +{moreCount} more
          </div>
        )}
      </td>
    ),

    order_total: (
      <td
        key="order_total"
        data-col="order_total"
        className={`${TDM} text-right font-semibold truncate`}
        data-testid={`${testIdPrefix}-total-${order.id}`}
      >
        {formatMoney(order.order_total ?? order.total_amount ?? 0)}
      </td>
    ),

    status: (
      <td key="status" data-col="status" className={TDM}>
        <div className="flex items-center gap-1 min-w-0">
          <StatusBadge status={order.status} order={order} className="truncate max-w-full" />
          {isComplete && (
            <span
              className="shrink-0 text-emerald-700 dark:text-emerald-300"
              title="Complete"
              data-testid="order-complete-badge"
            >
              ✓<span className="sr-only"> Complete</span>
            </span>
          )}
        </div>
      </td>
    ),
  };

  return (
    <>
      <tr
        onClick={(e) => onToggleExpand && onToggleExpand(order.id, e)}
        data-testid={rowTestId}
        data-mobile-row=""
        className={`cursor-pointer select-none transition-colors ${isExpanded ? EXPANDED_ROW_FRAME : ROW_HOVER} ${editingDim}`.trim()}
      >
        {columns.map((c) => cells[c.key] || null)}
      </tr>

      {/* Always rendered: `open` lets the panel ease CLOSED as well as open
          (`hooks/useExpandAnimation.js`); it renders nothing once closed. */}
      <ExpandedPanelRow
          open={isExpanded}
          colSpan={columns.length}
          panelWidth={panelWidth}
          testId={detailsTestId}
          isLoading={isLoadingDetail}
          detail={detail}
        >
          {/* 📱 The needed-by date rides the Order Details HEADER as "Due ● 08
              Aug 26" (owner 2026-10-03; "Due" — the desktop Date column's own
              word for this date — because "Needed by" did not fit at 360px). */}
          <OrderHeaderNoteContext.Provider
            value={{ note: !neededByInJob && order.required_by_date ? (
              <span
                className="inline-flex items-center gap-1 text-xs font-normal text-gray-600 dark:text-gray-300 whitespace-nowrap"
                data-testid={`${testIdPrefix}-mobile-needed-by-${order.id}`}
              >
                Due
                {neededByDot}
                <span className="font-medium">{formatDateCompact(order.required_by_date)}</span>
              </span>
            ) : null,
              // A tap on the row's extension line closes the order, like a tap on the row.
              onRowTap: onToggleExpand ? () => onToggleExpand(order.id) : null }}
          >
            <div data-mobile-compact="">{expandedContent}</div>
          </OrderHeaderNoteContext.Provider>
        </ExpandedPanelRow>
    </>
  );
}

/**
 * Table chrome — the horizontal scrollport, the `<colgroup>` and the header.
 * Rows go in as children so the page keeps its existing `.map()` and only
 * swaps which row component it returns.
 *
 * The scrollport is what keeps the PAGE from scrolling sideways on a narrow
 * screen: the table is at least TABLE_MIN_WIDTH wide and this box is
 * `overflow-x-auto`, so the overflow is consumed here.
 *
 * ── `w-full` + `minWidth` together, added 2026-08-08 ──────────────────
 * Product owner: *"department orders and to approve in table view should be
 * full width like cards view"*. The (since retired) card layout's list
 * container was a plain block `<div>`, so
 * cards filled 100% of the page container; a `<table>` is shrink-to-fit, so this
 * one rendered at exactly TABLE_MIN_WIDTH (1310px at the time) and left dead
 * whitespace to the right of every row on any wider screen.
 *
 * ⚠️ **Do not quote a pixel figure for TABLE_MIN_WIDTH — it is a `reduce` over
 * `ORDER_TABLE_COLUMNS` and it MOVES.** It was 1310 when the full-width change
 * landed and became 1430 the same day, when the PO column was added. The 1310
 * above is history, not a current value; the only truth is the constant.
 *
 * The two declarations do different jobs and BOTH are needed:
 *   - `w-full` makes the table fill the scrollport when there is room, and the
 *     spare width is distributed across every column in proportion to its
 *     `<colgroup>` width — the classic stretch-to-fit. That is the product
 *     owner's explicit choice between "spread it everywhere" and "give it to
 *     one or two columns"; he was shown both.
 *   - `style={{ minWidth: TABLE_MIN_WIDTH }}` keeps the floor, so a narrow
 *     screen still scrolls horizontally inside this box instead of crushing ten
 *     data columns and a chevron into 400px.
 *
 * 🔑 **Raising the floor does NOT put a scrollbar on the PAGE, and the reason is
 * width-independent — checked when PO took the floor from 1310 to 1430.** Two
 * things consume the overflow and neither knows the number: this box's
 * `overflow-x-auto`, and `min-w-0` on `<main>` (`components/Layout.js:1546`),
 * whose long comment records that removing that utility made `<main>` measure
 * the table's min-content width and gave the PAGE a horizontal scrollbar
 * (measured +200px at a 1280 viewport, +456px at 1024). The floor may rise
 * freely while both hold; if either goes, every width breaks at once. Pinned by
 * `Layout.stickyScrollport.test.js` and by §8 of
 * `OrderTableView.itemPreview.test.js` — as CLASS STRINGS only, so **whether the
 * page actually scrolls sideways at a given viewport is a browser check.**
 * ITEM_GRID's tracks are proportions precisely so the collapsed item preview
 * follows the stretch — read its docblock before touching either.
 *
 * ── Sortable headers, added 2026-08-08 ────────────────────────────────
 * `sortField` / `sortDir` / `onHeaderSort` are OPTIONAL. Omit all three and the
 * header renders exactly as it did before — plain, unclickable text — which is
 * what keeps a row rendered outside a page (and every existing test that mounts
 * the shell bare) working unchanged.
 *
 * 🔑 **`onHeaderSort` must be the page's EXISTING sort handler, not a new one.**
 * These headers are the only sort control now (the `Sort ▾` dropdown was
 * retired with the order cards, 2026-10-18), and a second sort state would be
 * a second answer to one question.
 * Both pages pass their `handleSort(field)`, which toggles direction when the
 * field is already active and otherwise selects it.
 */
export function OrderTableShell({
  children,
  testIdPrefix,
  /**
   * 🔀 WHICH COLUMNS TO RENDER — OPTIONAL, added 2026-08-12, defaulting to the
   * twelve. **Omit it and this shell is byte-identical to the one that shipped
   * before the prop existed**, which is what keeps `DepartmentOrders.js` and
   * `Approvals.js` untouched by the My Orders column change. Asserted rather
   * than claimed: `OrderTableView.columns.test.js` §1.
   *
   * 🚨 **PASS A FILTER OF `ORDER_TABLE_COLUMNS`, NEVER A HAND-WRITTEN ARRAY.**
   * A retyped model drifts the first time a width, a `min` or a cycle state is
   * retuned upstream, and `useColumnWidths` silently floors any column with no
   * `min` at ZERO. `MY_ORDERS_TABLE_COLUMNS` is the worked example.
   *
   * 🔑 **A caller passing this MUST also pass its own width key** — see
   * `MY_ORDERS_COL_WIDTHS_KEY`: sharing one key across two different column
   * sets corrupts the other surface's stored widths on the first drag.
   */
  columns = ORDER_TABLE_COLUMNS,
  // The ACTIVE sort, as the page holds it — a `sortField` value, not a column key.
  sortField = null,
  sortDir = 'asc',
  // `(sortField) => void`. When absent, no header is clickable at all.
  onHeaderSort = null,
  /**
   * 🔀 USER-DRAGGABLE COLUMN WIDTHS, added 2026-08-10. Product owner: *"to
   * approve and department order tables need the ability to have their column
   * widths adjusted like breakdown."*
   *
   * 🔑 **THE STATE LIVES ON THE PAGE, NOT HERE, for the reason `GagBreakdown`
   * records: `Fit columns to screen` (this shell's own `Reset column widths`
   * until 2026-09-28) is an `Actions ▾` item up in the header band.** This
   * shell takes the map as a prop and owns none of it; a second route into
   * one piece of state is how one width model quietly becomes two.
   *
   * 🚨 **ALL THREE ARE OPTIONAL AND OMITTING THEM IS BYTE-IDENTICAL TO THE
   * SHELL THAT SHIPPED BEFORE THIS FEATURE** — the `<colgroup>` falls back to
   * the declared widths, the floor falls back to `ORDER_TABLE_MIN_WIDTH`, the
   * item-preview grid falls back to the declared proportions, and no handle is
   * rendered at all. Same "omit the callback and the control is not offered"
   * shape as `onHeaderSort` above and as `GagGroupTable`'s identical trio.
   */
  columnWidths = null,
  onColumnResize = null,
  onColumnResetWidth = null,
  /**
   * 🔀 STICKY COLUMN HEADERS, added 2026-08-08. Product owner: *"make the table
   * header row sticky as well"*, then, seeing the first attempt: *"table headder
   * is now on top of the first item in the list"* and *"not sticky under the
   * search bar band where it should be"*.
   *
   * 🔀 Since 2026-09-26: the px offset the pinned HEADER PANE sticks at against
   * the PAGE — `useStickyToolbar()`'s **`headerTop`**, i.e. the app bar plus the
   * MEASURED toolbar height, so the column headings sit directly beneath the
   * toolbar. (Until then it sized a height-capped inner scroll region; see the
   * block below.) 🔑 **It must be the measured value, never a constant** — the
   * toolbar wraps to a second row on a narrow screen. Omit the prop and nothing
   * sticks and the shell is the single-table scrollport it always was, which
   * keeps a bare shell (and every test that mounts one) rendering as before.
   */
  stickyTop = null,
  // 🔀 `Fit columns to screen` (2026-09-28) — optional `(panelWidth) => void`,
  // forwarded straight to `PageScrollTable`'s own prop of the same name. The
  // page uses it to learn the measured scrollport width without a second
  // measurement. Omit it and nothing changes.
  onRegionWidthChange = null,
  // The order id holding unsaved inline edits — forwarded to `PageScrollTable`'s
  // `editingRowId`, which dims every OTHER row. Dept Orders only; Approvals and
  // My Orders pass nothing and dim nothing.
  editingRowId = null,
}) {
  /*
   * 🔀 THE BODY OF THIS SHELL MOVED, VERBATIM, TO `components/ui/PageScrollTable.js`
   * (2026-09-27) when the hires Table view needed the same page-scroll table —
   * the scroll-model docblock (why a sticky header cannot pin from inside an
   * x-scroller, the three panes, the ARIA over two tables) went with it; read
   * it there. What stays HERE is everything order-specific: the order columns,
   * the order width ceiling, the `order-col-resize` handle prefix, the expand
   * column's borrowed name, and the THREE row contexts the order rows read.
   * The DOM is byte-identical: every `OrderTableView.*` test ran unchanged.
   */
  return (
    <PageScrollTable
      phoneFullBleed
      testIdPrefix={testIdPrefix}
      columns={columns}
      sortField={sortField}
      sortDir={sortDir}
      onHeaderSort={onHeaderSort}
      columnWidths={columnWidths}
      onColumnResize={onColumnResize}
      onColumnResetWidth={onColumnResetWidth}
      stickyTop={stickyTop}
      maxColumnWidth={MAX_ORDER_COLUMN_WIDTH}
      resizeTestIdPrefix="order-col-resize"
      onRegionWidthChange={onRegionWidthChange}
      editingRowId={editingRowId}
      wrapRows={(rows, panelWidth) => (
        // THREE contexts, three different questions — see
        // `OrderTableWidthContext`, `OrderColumnWidthsContext` and
        // `OrderColumnsContext`. The rows are nodes the PAGE built, so none
        // of the three can reach them as a prop; `columns` is the SAME array
        // the header was built from, which is why it is a context.
        <OrderTableWidthContext.Provider value={panelWidth}>
          <OrderColumnWidthsContext.Provider value={columnWidths}>
            <OrderColumnsContext.Provider value={columns}>
              {rows}
            </OrderColumnsContext.Provider>
          </OrderColumnWidthsContext.Provider>
        </OrderTableWidthContext.Provider>
      )}
    >
      {children}
    </PageScrollTable>
  );
}

export default OrderTableShell;
